import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuid } from 'uuid';
import { useStore } from '../store/useStore';
import { createMarkup, deleteMarkup, getMarkups, updateMarkup as updateMarkupApi, getSheetThumbnailUrl, getFileDownloadUrl } from '../utils/api';
import FileViewer from './FileViewer';
import {
  formatLength, formatArea, formatVolume, distance, polylineLength,
  polygonArea, polygonCentroid,
} from '../utils/measure';
import toast from 'react-hot-toast';
import styles from './MarkupCanvas.module.css';

// ── Tool classification ──────────────────────────────────────────────────────
// Drag-to-draw measurement tools: single stroke from down → up.
const MEASURE_DRAG_TOOLS = new Set([
  'measure-freeline',
  'measure-rect-area', 'measure-freeform-area',
  'measure-rect-volume', 'measure-freeform-volume',
]);
// Click-to-add-vertex measurement tools: multi-click, dbl-click/Enter to finish.
// Click-to-add-vertex tools: each pointerdown adds a vertex; Enter or
// double-click finalizes; Escape cancels. Mix of measure tools and the
// regular polyline / polygon markups — they share the same state-machine
// pattern.
const CLICK_VERTEX_TOOLS = new Set([
  'polyline',
  'polygon',
  'measure-polyline',
  'measure-polygon-area',
  'measure-polygon-volume',
]);
// Two-point click-to-click tools: first click plants the anchor, second click
// commits at the current cursor position. No drag / no pointer capture.
const TWO_POINT_CLICK_TOOLS = new Set(['arrow', 'line', 'measure-line']);

// Tool id → { type, kind }. `type` is the DB type stored on the markup row;
// `kind` is the `data.kind` subfield that drives rendering & math (measure
// tools share `measure-length`/`measure-area`/`measure-volume` types and
// distinguish via kind; the regular polyline has its own type).
const CLICK_VERTEX_TOOL_MAP = {
  'polyline':                  { type: 'polyline',       kind: 'polyline' },
  'polygon':                   { type: 'polygon',        kind: 'polygon'  },
  'measure-line':              { type: 'measure-length', kind: 'line'     },
  'measure-polyline':          { type: 'measure-length', kind: 'polyline' },
  'measure-freeline':          { type: 'measure-length', kind: 'freeline' },
  'measure-rect-area':         { type: 'measure-area',   kind: 'rect'     },
  'measure-polygon-area':      { type: 'measure-area',   kind: 'polygon'  },
  'measure-freeform-area':     { type: 'measure-area',   kind: 'freeform' },
  'measure-rect-volume':       { type: 'measure-volume', kind: 'rect'     },
  'measure-polygon-volume':    { type: 'measure-volume', kind: 'polygon'  },
  'measure-freeform-volume':   { type: 'measure-volume', kind: 'freeform' },
};

export default function MarkupCanvas({ pageWidth, pageHeight, zoom, readOnly, selectedId, onSelectChange }) {
  const {
    markups, addMarkup, removeMarkup, updateMarkup, setMarkups,
    activeTool, setActiveTool,
    activeColor, activeFillColor, activeFillOpacity, activeSheet, role,
    calibrationPickPoints, addCalibrationPickPoint,
    sheets, setActiveSheet,
    markupClipboard, setMarkupClipboard,
  } = useStore();

  const svgRef   = useRef(null);
  // Timestamp of the most recent click-to-add-vertex pointerdown. Used by
  // the click-vertex finalize gesture: a second click landing near the
  // last vertex within FINALIZE_TIME counts as a double-click/double-tap
  // and ends the shape (works on iPad where browser dblclick is unreliable).
  const lastVertexTimeRef = useRef(0);
  const [drawing, setDrawing]     = useState(false);
  const [current, setCurrent]     = useState(null);

  // Marquee (rectangle drag-select). When the 'select' tool is active,
  // pointerdown starts a marquee; pointerup commits the set of intersected
  // markups into multiSelectIds. multiSelectIds is what drives multi-select
  // delete / copy gestures + the rendered selection outline on each
  // matched markup. Both clear when the user single-clicks a markup or
  // background, or switches sheets.
  const [marquee, setMarquee] = useState(null); // { x, y, w, h } | null
  const [multiSelectIds, setMultiSelectIds] = useState(() => new Set());
  const [textInput, setTextInput] = useState(null);
  // Cloud-callout in-progress: between pointerup of the cloud drag and
  // submit of the text input. Captures the user-drawn cloud rect so
  // submitText can splice it into the persisted callout markup.
  const [cloudCalloutDraft, setCloudCalloutDraft] = useState(null);
  // shape: { cloud: {x,y,w,h}, color, fill_color, fill_opacity, stroke_width }

  // Plain text drag-out: same pattern as cloudCalloutDraft but for the
  // text tool. User drags a box, releases, types in the box. The saved
  // markup is `text` type with x/y/w/h/text in data — the box stays
  // around the text so the markup can be reselected and moved later.
  const [textDraft, setTextDraft] = useState(null);
  // shape: { box: {x,y,w,h}, color, fill_color, fill_opacity, stroke_width }

  // Regular callout one-click: click point becomes the arrow tip; box
  // and leader auto-render at sensible defaults; user types inside the
  // box. Captures the click (leader endpoint) so submitText can build
  // the final callout with the box at its computed offset.
  const [calloutDraft, setCalloutDraft] = useState(null);
  // shape: { leader: {x,y}, color, fill_color, fill_opacity, stroke_width }
  // Tracks the live cursor position during multi-click tools (polyline) so we
  // can render a rubber-band preview from the last committed vertex.
  const [cursorPos, setCursorPos] = useState(null);
  // Hyperlink preview state — populated when the cursor is over a hyperlink
  // markup so we can render a floating thumbnail card of the target sheet.
  const [hoveredHyperlink, setHoveredHyperlink] = useState(null); // { markup, clientX, clientY }

  // Linked-file preview state — same idea as hyperlinks, but for files
  // attached via the markup's linked_file_id. `viewingFile` opens an in-app
  // FileViewer modal with that file's contents.
  const [hoveredLink, setHoveredLink] = useState(null); // { markup, clientX, clientY }
  const [viewingFile, setViewingFile] = useState(null); // file object | null

  // Linked-sheet preview state — same pattern, parallel slot. Click on a
  // linked-sheet badge navigates to the target sheet in a new tab via the
  // `?sheet=<id>` deep link (same mechanism hyperlinks use), so cross-
  // references don't lose the current tab's context.
  const [hoveredSheetLink, setHoveredSheetLink] = useState(null);

  // Hyperlinks only render when the SOURCE sheet is published (or has no
  // version set at all). Drafts suppress rendering entirely — detection
  // still happens on upload, but clicks don't activate until publish.
  const hyperlinksVisible = !activeSheet?.version_set_id || activeSheet?.is_published === true;

  const calibration = activeSheet?.calibration || null;
  const isCalibrating = activeTool === 'calibrate';

  const editRef          = useRef(null);   // active drag / resize interaction
  const suppressClickRef = useRef(false);  // swallow click that follows a drag

  const canDelete = role === 'admin' || role === 'pm';
  // Editing (move/resize) an existing markup is allowed for any signed-in
  // role — admin / pm / super edit shared drafts, field can edit their own
  // private markups. The server's visibility filter and POST handler keep
  // field-authored markups invisible to others, so this flag being true for
  // field is safe.
  const canEdit   = !!role;

  // Load markups when sheet changes
  useEffect(() => {
    if (!activeSheet) { setMarkups([]); return; }
    getMarkups({ sheet_id: activeSheet.id })
      .then((rows) => setMarkups(rows))
      .catch((err) => console.error('Failed to load markups', err));
    // A multi-selection from the prior sheet doesn't apply to the new one.
    setMultiSelectIds(new Set());
    setMarquee(null);
  }, [activeSheet?.id]);

  // The server-side GET /api/markups already filters out anything the
  // current viewer shouldn't see (private-to-creator markups by other
  // users, non-public drafts when the viewer is a field user, etc.). No
  // additional client-side filter needed.
  const visibleMarkups = markups;

  function svgPoint(e) {
    const svg  = svgRef.current;
    const rect = svg.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left)  / zoom,
      y: (e.clientY - rect.top)   / zoom,
    };
  }

  function onPointerDown(e) {
    if (readOnly || activeTool === 'pan') return;
    e.preventDefault();
    // PdfViewer's container has its own pointerdown handler for pan/pinch.
    // Stop propagation so it doesn't also try to start a pan gesture on top
    // of whatever we're drawing. (Pan tool returns early above, so the
    // container still gets pan-mode events via normal bubbling.)
    e.stopPropagation();
    const pt = svgPoint(e);

    // ── Marquee selection tool ────────────────────────────────────────
    // 'select' is a non-drawing tool: drag a rectangle, release commits
    // the set of intersected markups into multiSelectIds. Pointer
    // capture so a fast drag that leaves the SVG still gets the up.
    if (activeTool === 'select') {
      svgRef.current.setPointerCapture(e.pointerId);
      onSelectChange(null);
      setMultiSelectIds(new Set());
      setMarquee({ x: pt.x, y: pt.y, w: 0, h: 0 });
      return;
    }

    // Calibration pick: collect 2 points, then the store auto-reopens the modal.
    if (isCalibrating) {
      addCalibrationPickPoint(pt);
      return;
    }

    // ── Two-point click-to-click tools (arrow / line / measure-line) ──────
    // First click plants the anchor; second click commits at the cursor.
    // No pointer capture, no `drawing` flag — those distinguish click-click
    // from drag on the same shape types.
    if (TWO_POINT_CLICK_TOOLS.has(activeTool)) {
      if (isTwoPointClickInProgress(current, drawing)) {
        // Second click — snap endpoint to the click pt and persist.
        let snapped;
        if (current.type === 'arrow' || current.type === 'line') {
          snapped = { ...current, x2: pt.x, y2: pt.y };
        } else {
          snapped = { ...current, data: { ...current.data, x2: pt.x, y2: pt.y } };
        }
        setCurrent(snapped);
        if (isTrivial(snapped)) { setCurrent(null); return; }
        persistCurrent(snapped);
        return;
      }
      // First click — start preview shape.
      onSelectChange(null);
      const firstClickFill = (!activeFillColor || activeFillColor === 'transparent') ? null : activeFillColor;
      if (activeTool === 'arrow' || activeTool === 'line') {
        setCurrent({
          type: activeTool,
          x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y,
          color: activeColor, fill_color: firstClickFill,
        });
      } else {
        // measure-line
        setCurrent({
          type: 'measure-length', color: activeColor,
          data: { kind: 'line', x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y },
        });
      }
      return;
    }

    // ── Click-to-add-vertex tools (polyline / polygon) ───────────────────
    // These don't use pointer capture — multiple pointerdowns across moves.
    if (CLICK_VERTEX_TOOLS.has(activeTool)) {
      const m = CLICK_VERTEX_TOOL_MAP[activeTool];
      if (current && current.type === m.type && current.data?.kind === m.kind) {
        // Finalize gesture: two clicks landing near each other within a
        // short time window (≈ a double-click or iPad double-tap) end
        // the shape. We do this on pointerdown rather than waiting for
        // the browser's dblclick event because iPad touch doesn't fire
        // dblclick reliably. Distance + time both required so a single
        // slow click near a previous vertex doesn't accidentally end
        // the shape. Polygons close back to v1 via the 'Z' in the
        // rendered path; polylines just persist the open segment chain.
        const FINALIZE_DIST = 8;
        const FINALIZE_TIME = 400; // ms — covers typical iPad double-tap
        const pts = current.data.points || [];
        const last = pts[pts.length - 1];
        const now  = Date.now();
        const isDoubleTap =
          last
          && Math.hypot(pt.x - last.x, pt.y - last.y) < FINALIZE_DIST
          && (now - lastVertexTimeRef.current) < FINALIZE_TIME;
        if (isDoubleTap) {
          finalizeMultiClick();
          return;
        }
        lastVertexTimeRef.current = now;
        // Append a new vertex to the in-progress shape.
        setCurrent((c) => ({
          ...c,
          data: { ...c.data, points: [...c.data.points, pt] },
        }));
      } else {
        // Start a new click-to-add-vertex shape. Volume shapes need a
        // default depth so the label renders something useful until the
        // user sets one in the popup.
        onSelectChange(null);
        const baseData = { kind: m.kind, points: [pt] };
        if (m.type === 'measure-volume') baseData.depthFt = 1;
        setCurrent({
          type: m.type,
          color: activeColor,
          fill_color: m.type !== 'measure-length' ? (activeFillColor || null) : null,
          data: baseData,
        });
        lastVertexTimeRef.current = Date.now();
      }
      return;
    }

    svgRef.current.setPointerCapture(e.pointerId);

    // Regular callout: one click. The click point becomes the arrow
    // tip; the text box auto-positions up-and-left of it; the leader
    // is auto-routed between them. User types inside the box.
    if (activeTool === 'callout') {
      const fillColor = (!activeFillColor || activeFillColor === 'transparent') ? null : activeFillColor;
      const W = 140, H = 40;
      // Box origin offset up-and-left from the click. Numbers tuned so
      // the leader runs at a comfortable diagonal.
      const boxX = pt.x - W - 80;
      const boxY = pt.y - H - 40;
      setCalloutDraft({
        leader: { x: pt.x, y: pt.y },
        color:  activeColor,
        fill_color: fillColor,
        fill_opacity: fillColor ? activeFillOpacity : 1,
        stroke_width: 2,
      });
      // textInput is the box top-left for the callout-variant input.
      setTextInput({ x: boxX, y: boxY });
      return;
    }
    // ('text' and 'cloud-callout' use a drag-draw flow instead — see
    // the switch below.)
    setDrawing(true);
    onSelectChange(null);

    const fillColor = (!activeFillColor || activeFillColor === 'transparent') ? null : activeFillColor;

    switch (activeTool) {
      // NOTE: arrow / line / measure-line are handled by the two-point
      // click-to-click branch above and never reach this switch.
      case 'rect':
      case 'ellipse':
      case 'cloud':
      case 'cloud-callout':
      case 'text':
        // 'text' uses the same drag-draw shape as rect/cloud during the
        // drag phase. On pointerup it transitions to text-edit instead
        // of persisting as a rect (see onPointerUp). 'ellipse' shares
        // the same x/y/w/h bounding box; only the renderer differs.
        setCurrent({ type: activeTool, x: pt.x, y: pt.y, w: 0, h: 0, color: activeColor, fill_color: fillColor });
        break;
      case 'freehand':
        setCurrent({ type: 'freehand', points: [pt], color: activeColor, fill_color: fillColor });
        break;
      case 'measure':
        setCurrent({ type: 'measure', x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y, color: activeColor });
        break;
      case 'measure-freeline':
        setCurrent({
          type: 'measure-length', color: activeColor,
          data: { kind: 'freeline', points: [pt] },
        });
        break;
      case 'measure-rect-area':
        setCurrent({
          type: 'measure-area', color: activeColor, fill_color: fillColor,
          data: { kind: 'rect', x: pt.x, y: pt.y, w: 0, h: 0 },
        });
        break;
      case 'measure-freeform-area':
        setCurrent({
          type: 'measure-area', color: activeColor, fill_color: fillColor,
          data: { kind: 'freeform', points: [pt] },
        });
        break;
      case 'measure-rect-volume':
        setCurrent({
          type: 'measure-volume', color: activeColor, fill_color: fillColor,
          data: { kind: 'rect', x: pt.x, y: pt.y, w: 0, h: 0, depthFt: 1 },
        });
        break;
      case 'measure-freeform-volume':
        setCurrent({
          type: 'measure-volume', color: activeColor, fill_color: fillColor,
          data: { kind: 'freeform', points: [pt], depthFt: 1 },
        });
        break;
      default: break;
    }
  }

  function onPointerMove(e) {
    const pt = svgPoint(e);
    // Marquee drag — update its w/h while the pointer moves; raw (signed)
    // values, normalized only at commit/render time.
    if (marquee) {
      setMarquee((m) => ({ ...m, w: pt.x - m.x, h: pt.y - m.y }));
      return;
    }
    // Always track cursor for multi-click previews (polyline / polygon).
    if (current && isMultiClickInProgress(current)) {
      setCursorPos(pt);
    }
    // Two-point click-to-click: endpoint follows the cursor between clicks.
    if (current && isTwoPointClickInProgress(current, drawing)) {
      if (current.type === 'arrow' || current.type === 'line') {
        setCurrent((c) => ({ ...c, x2: pt.x, y2: pt.y }));
      } else if (current.type === 'measure-length') {
        setCurrent((c) => ({ ...c, data: { ...c.data, x2: pt.x, y2: pt.y } }));
      }
      return;
    }
    if (!drawing || !current) return;
    switch (current.type) {
      case 'arrow': case 'line': case 'measure':
        setCurrent((c) => ({ ...c, x2: pt.x, y2: pt.y }));
        break;
      case 'rect': case 'ellipse': case 'cloud': case 'cloud-callout': case 'text':
        setCurrent((c) => ({ ...c, w: pt.x - current.x, h: pt.y - current.y }));
        break;
      case 'freehand':
        setCurrent((c) => ({ ...c, points: [...c.points, pt] }));
        break;
      case 'measure-length': {
        if (current.data.kind === 'line') {
          setCurrent((c) => ({ ...c, data: { ...c.data, x2: pt.x, y2: pt.y } }));
        } else if (current.data.kind === 'freeline') {
          setCurrent((c) => ({ ...c, data: { ...c.data, points: [...c.data.points, pt] } }));
        }
        break;
      }
      case 'measure-area':
      case 'measure-volume': {
        if (current.data.kind === 'rect') {
          setCurrent((c) => ({ ...c, data: { ...c.data, w: pt.x - c.data.x, h: pt.y - c.data.y } }));
        } else if (current.data.kind === 'freeform') {
          setCurrent((c) => ({ ...c, data: { ...c.data, points: [...c.data.points, pt] } }));
        }
        break;
      }
      default: break;
    }
  }

  async function onPointerUp() {
    // Marquee commit — compute which visible markups intersect the
    // dragged rectangle, populate multiSelectIds, return to pan tool
    // so subsequent gestures don't keep drawing marquees. A trivially
    // small rectangle (basically a click without drag) just clears
    // multi-selection without committing.
    if (marquee) {
      const [mx, my, mw, mh] = normalizeRect(marquee.x, marquee.y, marquee.w, marquee.h);
      setMarquee(null);
      if (mw < 3 && mh < 3) {
        setMultiSelectIds(new Set());
        setActiveTool('pan');
        return;
      }
      const rect = { x: mx, y: my, w: mw, h: mh };
      const ids = new Set(
        visibleMarkups
          .filter((m) => bboxOverlapsRect(getMarkupBBox(m), rect))
          .map((m) => m.id)
      );
      setMultiSelectIds(ids);
      setActiveTool('pan');
      return;
    }

    if (!drawing || !current) return;
    setDrawing(false);
    if (isTrivial(current)) { setCurrent(null); return; }

    // Cloud-callout: pointerup ends the cloud-drawing phase and opens
    // the text input. The cloud rect is stashed in cloudCalloutDraft so
    // submitText can splice it into the final markup. The text input is
    // anchored to the right of the cloud (centered vertically) so the
    // leader has somewhere natural to point from.
    if (current.type === 'cloud-callout') {
      const [nx, ny, nw, nh] = normalizeRect(current.x, current.y, current.w, current.h);
      setCloudCalloutDraft({
        cloud: { x: nx, y: ny, w: nw, h: nh },
        color:        current.color,
        fill_color:   current.fill_color,
        fill_opacity: current.fill_opacity ?? 1,
        stroke_width: current.stroke_width ?? 2,
      });
      // textInput is the BOX TOP-LEFT (not click center) for the callout
      // variant — TextInputOverlay's callout variant places the input from
      // its top-left, sized exactly to match the eventual 140×40 box.
      // Cloud right + 30 px gap, vertically centered with the cloud.
      setTextInput({ x: nx + nw + 30, y: ny + nh / 2 - 20 });
      setCurrent(null);
      return;
    }

    if (current.type === 'text') {
      // Plain-text drag-out: capture the dragged box and open the text
      // input filling it. submitText reads textDraft to know which
      // dimensions / colors to bake into the saved text markup.
      const [nx, ny, nw, nh] = normalizeRect(current.x, current.y, current.w, current.h);
      setTextDraft({
        box: { x: nx, y: ny, w: nw, h: nh },
        color:        current.color,
        fill_color:   current.fill_color,
        fill_opacity: current.fill_opacity ?? 1,
        stroke_width: current.stroke_width ?? 2,
      });
      setTextInput({ x: nx, y: ny });  // box top-left — input fills the box
      setCurrent(null);
      return;
    }

    await persistCurrent();
  }

  // ── Multi-click completion (polyline / polygon) ───────────────────────────
  async function finalizeMultiClick() {
    if (!current) return;
    const minPoints = (current.data?.kind === 'polygon') ? 3 : 2;
    if ((current.data?.points?.length || 0) < minPoints) {
      setCurrent(null);
      setCursorPos(null);
      return;
    }
    setCursorPos(null);
    await persistCurrent();
  }

  function cancelCurrent() {
    setCurrent(null);
    setCursorPos(null);
    setDrawing(false);
  }

  // Escape cancels any in-progress shape; Enter finalizes multi-click shapes.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && current) {
        e.preventDefault();
        cancelCurrent();
      } else if (e.key === 'Enter' && isMultiClickInProgress(current)) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        finalizeMultiClick();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [current]);

  // If the user switches tools mid-shape, drop the in-progress shape.
  useEffect(() => {
    if (isMultiClickInProgress(current)) {
      const m = CLICK_VERTEX_TOOL_MAP[activeTool];
      if (!m || m.type !== current.type || m.kind !== current.data.kind) cancelCurrent();
    } else if (isTwoPointClickInProgress(current, drawing)) {
      // Cancel if the new tool doesn't match the in-progress 2pt-click shape.
      const stillMatches =
        (activeTool === 'arrow' && current.type === 'arrow') ||
        (activeTool === 'line'  && current.type === 'line')  ||
        (activeTool === 'measure-line' && current.type === 'measure-length' && current.data?.kind === 'line');
      if (!stillMatches) cancelCurrent();
    }
    // Entering marquee (Select) mode: drop any single-select so the
    // selected markup's body doesn't intercept the first pointerdown
    // (its `canMove` handler would `stopPropagation` and start a move
    // drag instead of letting the SVG-level marquee branch fire).
    // Also reset multiSelectIds so the user starts a fresh marquee.
    if (activeTool === 'select') {
      if (selectedId) onSelectChange(null);
      if (multiSelectIds.size > 0) setMultiSelectIds(new Set());
    }
    // eslint-disable-next-line
  }, [activeTool]);

  // Shared: persist the `current` shape to the server and promote it to a
  // stored markup (optimistically with a temp uuid, reconciled on success).
  // Callers may pass an explicit shape; falls back to the state `current`
  // for legacy call sites (onPointerUp, finalizeMultiClick).
  async function persistCurrent(shapeArg) {
    const src = shapeArg || current;
    if (!src) return;
    suppressClickRef.current = true;
    setTimeout(() => { suppressClickRef.current = false; }, 120);

    const fillColor = src.fill_color || null;
    const markup = {
      id: uuid(),
      type: src.type,
      color: src.color,
      fill_color: fillColor,
      fill_opacity: fillColor ? activeFillOpacity : 1,
      stroke_width: 2,
      data: shapeToData(src),
      author: role,
      author_role: role,
    };

    addMarkup(markup);
    setCurrent(null);
    // Auto-switch back to pan so the user doesn't have to manually re-select
    // pan after each markup. They can re-pick the tool if they want another.
    setActiveTool('pan');
    onSelectChange(markup.id);

    try {
      const saved = await createMarkup({
        sheet_id:     activeSheet.id,
        type:         markup.type,
        color:        markup.color,
        fill_color:   markup.fill_color,
        fill_opacity: markup.fill_opacity,
        stroke_width: markup.stroke_width,
        data:         markup.data,
        author:       markup.author,
        author_role:  markup.author_role,
      });
      removeMarkup(markup.id);
      addMarkup({ ...markup, ...saved });
      if (saved.id && saved.id !== markup.id) onSelectChange(saved.id);
    } catch (err) {
      removeMarkup(markup.id);
      onSelectChange(null);
      toast.error('Failed to save markup');
    }
  }

  async function submitText(value) {
    if (!textInput) return;
    const trimmed = value.trim();

    // Empty submit on any in-progress draft → discard everything (the
    // user set up a markup but typed nothing). Same effect as Escape.
    if ((cloudCalloutDraft || textDraft || calloutDraft) && !trimmed) {
      setTextInput(null);
      setCloudCalloutDraft(null);
      setTextDraft(null);
      setCalloutDraft(null);
      setActiveTool('pan');
      return;
    }
    if (!trimmed) { setTextInput(null); return; }

    const fillColor = (!activeFillColor || activeFillColor === 'transparent') ? null : activeFillColor;

    // Build markup based on which text tool is active. Plain 'text' is a
    // single x/y/text record; callouts wrap the same text in a positioned
    // box with a leader pointing somewhere off the box.
    let markup;
    if (cloudCalloutDraft) {
      // Cloud-callout finalize: the user already drew the cloud rect on
      // pointerup; the text input is anchored to the right of it and we
      // splice both into the persisted markup. Inherits color/fill from
      // the in-progress cloud (they were captured from active* at the
      // moment of pointerdown). textInput coords are the box TOP-LEFT
      // for cloud-callouts (callout variant of TextInputOverlay).
      const W = 140, H = 40;
      const box = {
        x: textInput.x,
        y: textInput.y,
        w: W, h: H,
      };
      const data = {
        kind: 'cloud',
        box,
        cloud: cloudCalloutDraft.cloud,
        text: trimmed,
        fontSize: 14,
      };
      markup = {
        id: uuid(),
        type: 'callout',
        color:        cloudCalloutDraft.color        || activeColor,
        fill_color:   cloudCalloutDraft.fill_color   || fillColor,
        fill_opacity: cloudCalloutDraft.fill_opacity ?? (fillColor ? activeFillOpacity : 1),
        stroke_width: cloudCalloutDraft.stroke_width ?? 2,
        data,
        author: role, author_role: role,
      };
      setCloudCalloutDraft(null);
    } else if (calloutDraft) {
      // Rect callout (one-click flow): the click point IS the arrow tip
      // (calloutDraft.leader). The text box was placed up-and-left of it
      // at pointerdown time; textInput coords are the box top-left.
      const W = 140, H = 40;
      const box = {
        x: textInput.x,
        y: textInput.y,
        w: W, h: H,
      };
      const data = {
        kind: 'rect',
        box,
        text: trimmed,
        fontSize: 14,
        leader: calloutDraft.leader,
      };
      markup = {
        id: uuid(),
        type: 'callout',
        color:        calloutDraft.color        || activeColor,
        fill_color:   calloutDraft.fill_color   || fillColor,
        fill_opacity: calloutDraft.fill_opacity ?? (fillColor ? activeFillOpacity : 1),
        stroke_width: calloutDraft.stroke_width ?? 2,
        data,
        author: role, author_role: role,
      };
      setCalloutDraft(null);
    } else if (textDraft) {
      // Plain-text drag-out: persist as a text markup with box dimensions
      // so MarkupShape renders the text inside the dragged rectangle
      // (wrapped + centered, like callout boxes).
      const { box } = textDraft;
      markup = {
        id: uuid(),
        type: 'text',
        color:        textDraft.color        || activeColor,
        fill_color:   textDraft.fill_color   || fillColor,
        fill_opacity: textDraft.fill_opacity ?? (fillColor ? activeFillOpacity : 1),
        stroke_width: textDraft.stroke_width ?? 2,
        data: { x: box.x, y: box.y, w: box.w, h: box.h, text: trimmed, fontSize: 14 },
        author: role, author_role: role,
      };
      setTextDraft(null);
    } else {
      markup = {
        id: uuid(),
        type: 'text',
        color: activeColor,
        fill_color: fillColor,
        fill_opacity: fillColor ? activeFillOpacity : 1,
        stroke_width: 2,
        data: { x: textInput.x, y: textInput.y, text: trimmed },
        author: role, author_role: role,
      };
    }
    addMarkup(markup);
    setTextInput(null);
    // Auto-switch to pan + auto-select so the move cursor + selection outline
    // appear right away.
    setActiveTool('pan');
    onSelectChange(markup.id);
    try {
      const saved = await createMarkup({
        sheet_id:     activeSheet.id,
        type:         markup.type,
        color:        markup.color,
        fill_color:   markup.fill_color,
        fill_opacity: markup.fill_opacity,
        stroke_width: markup.stroke_width,
        data:         markup.data,
        author:       markup.author,
        author_role:  markup.author_role,
      });
      removeMarkup(markup.id);
      addMarkup({ ...markup, ...saved });
      if (saved.id && saved.id !== markup.id) onSelectChange(saved.id);
    } catch (err) {
      removeMarkup(markup.id);
      onSelectChange(null);
      toast.error('Failed to save markup');
    }
  }

  // Keyboard shortcuts — Delete/Backspace, Ctrl/Cmd+C, Ctrl/Cmd+V.
  // Multi-selection (marquee) and single selection share the same
  // shortcuts; we just collect the relevant ids first and dispatch.
  // Copy/paste are gated by the markup's `published` flag — once a
  // markup is published it's canonical and not eligible for cloning.
  useEffect(() => {
    function onKey(e) {
      // Don't fire shortcuts while typing in an input / textarea.
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const mod = e.ctrlKey || e.metaKey;

      // ── Copy ──
      if (mod && (e.key === 'c' || e.key === 'C')) {
        const ids = multiSelectIds.size > 0
          ? [...multiSelectIds]
          : (selectedId ? [selectedId] : []);
        if (!ids.length) return;
        const eligible = markups.filter((m) =>
          ids.includes(m.id) && !m.published && m.type !== 'hyperlink'
        );
        if (!eligible.length) {
          toast('Only unpublished markups can be copied');
          return;
        }
        e.preventDefault();
        // Strip id + sheet_id + author timestamps; keep visual + data
        // payload. Paste re-stamps these for the active sheet + user.
        const payload = eligible.map((m) => ({
          type:         m.type,
          color:        m.color,
          fill_color:   m.fill_color,
          stroke_width: m.stroke_width,
          fill_opacity: m.fill_opacity,
          data:         m.data,
          note:         m.note,
        }));
        setMarkupClipboard(payload);
        toast.success(`Copied ${eligible.length} markup${eligible.length === 1 ? '' : 's'}`);
        return;
      }

      // ── Paste ──
      if (mod && (e.key === 'v' || e.key === 'V')) {
        if (!canEdit || !activeSheet || !markupClipboard.length) return;
        e.preventDefault();
        pasteClipboard();
        return;
      }

      // ── Delete/Backspace (single or multi) ──
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!canDelete) return;
        const ids = multiSelectIds.size > 0
          ? [...multiSelectIds]
          : (selectedId ? [selectedId] : []);
        if (!ids.length) return;
        e.preventDefault();
        handleDeleteMany(ids);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, multiSelectIds, canDelete, canEdit, markups, markupClipboard, activeSheet]);

  async function handleDeleteMany(ids) {
    // Optimistic removal first so the UI updates instantly. If a server
    // delete fails we re-add (best effort) so the user sees something
    // sane; a hard refetch on failure would also work.
    const snapshot = markups.filter((m) => ids.includes(m.id));
    ids.forEach((id) => removeMarkup(id));
    onSelectChange(null);
    setMultiSelectIds(new Set());
    const results = await Promise.allSettled(ids.map((id) => deleteMarkup(id)));
    const failed = results
      .map((r, i) => (r.status === 'rejected' ? snapshot[i] : null))
      .filter(Boolean);
    if (failed.length) {
      failed.forEach((m) => addMarkup(m));
      toast.error(`Failed to delete ${failed.length} markup${failed.length === 1 ? '' : 's'}`);
    }
  }

  async function pasteClipboard() {
    const DX = 20, DY = 20;
    // Build optimistic + offset data for each clipboard entry, then
    // POST in parallel. Each gets a temp uuid that we swap for the
    // server-assigned id once it lands.
    const items = markupClipboard.map((stash) => {
      const offsetData = computeEditData(stash.type, 'move', stash.data, DX, DY);
      const tempId = uuid();
      return {
        tempId,
        stash,
        offsetData,
        optimistic: {
          id:           tempId,
          sheet_id:     activeSheet.id,
          project_id:   activeSheet.project_id,
          type:         stash.type,
          color:        stash.color,
          fill_color:   stash.fill_color,
          stroke_width: stash.stroke_width,
          fill_opacity: stash.fill_opacity,
          data:         offsetData,
          note:         stash.note,
          author:       role,
          author_role:  role,
          published:    false,
        },
      };
    });
    items.forEach((it) => addMarkup(it.optimistic));

    const results = await Promise.allSettled(items.map((it) => createMarkup({
      sheet_id:     activeSheet.id,
      type:         it.stash.type,
      color:        it.stash.color,
      fill_color:   it.stash.fill_color,
      stroke_width: it.stash.stroke_width,
      fill_opacity: it.stash.fill_opacity,
      data:         it.offsetData,
      note:         it.stash.note,
      author:       role,
      author_role:  role,
    })));

    const savedIds = new Set();
    let failedCount = 0;
    results.forEach((r, i) => {
      const it = items[i];
      removeMarkup(it.tempId);
      if (r.status === 'fulfilled' && r.value?.id) {
        addMarkup({ ...it.optimistic, ...r.value });
        savedIds.add(r.value.id);
      } else {
        failedCount += 1;
      }
    });
    if (failedCount) toast.error(`Failed to paste ${failedCount} markup${failedCount === 1 ? '' : 's'}`);

    // Select what landed: single → selectedId; many → multiSelectIds.
    if (savedIds.size === 1) {
      onSelectChange([...savedIds][0]);
      setMultiSelectIds(new Set());
    } else if (savedIds.size > 1) {
      onSelectChange(null);
      setMultiSelectIds(savedIds);
    }
  }

  // ── Edit (drag / resize) ───────────────────────────────────────
  const startEdit = useCallback((markup, mode, clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect0 = svg.getBoundingClientRect();
    const startPt = {
      x: (clientX - rect0.left) / zoom,
      y: (clientY - rect0.top)  / zoom,
    };
    editRef.current = {
      id: markup.id,
      type: markup.type,
      mode,
      startPt,
      startData: JSON.parse(JSON.stringify(markup.data || {})),
      moved: false,
    };

    function onMove(e) {
      if (!editRef.current) return;
      const svgCur = svgRef.current;
      if (!svgCur) return;
      const rect = svgCur.getBoundingClientRect();
      const pt = {
        x: (e.clientX - rect.left) / zoom,
        y: (e.clientY - rect.top)  / zoom,
      };
      const { id, type, mode, startPt, startData } = editRef.current;
      const dx = pt.x - startPt.x;
      const dy = pt.y - startPt.y;
      if (Math.abs(dx) + Math.abs(dy) > 0.5) editRef.current.moved = true;
      const newData = computeEditData(type, mode, startData, dx, dy);
      updateMarkup(id, { data: newData });
    }

    async function onUp() {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      const cur = editRef.current;
      editRef.current = null;
      if (!cur) return;
      if (cur.moved) {
        suppressClickRef.current = true;
        setTimeout(() => { suppressClickRef.current = false; }, 120);
        const latest = useStore.getState().markups.find((m) => m.id === cur.id);
        if (latest) {
          try {
            await updateMarkupApi(cur.id, { data: latest.data });
          } catch (err) {
            toast.error('Failed to save changes');
          }
        }
      }
    }

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [zoom, updateMarkup]);

  const svgW = pageWidth  * zoom;
  const svgH = pageHeight * zoom;

  return (
    <>
      <svg
        ref={svgRef}
        className={styles.svg}
        width={svgW}
        height={svgH}
        style={{ pointerEvents: 'all' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={(e) => {
          // Finish a multi-click measurement on double-click (polyline / polygon).
          if (isMultiClickInProgress(current)) {
            e.preventDefault();
            // The 2nd click already appended a vertex at the dbl-click point;
            // trim it off so the endpoint is the 1st click of the pair.
            const pts = current.data.points || [];
            const min = current.data.kind === 'polygon' ? 4 : 3;
            if (pts.length >= min) {
              setCurrent((c) => ({
                ...c,
                data: { ...c.data, points: pts.slice(0, -1) },
              }));
            }
            finalizeMultiClick();
          }
        }}
        onClick={(e) => {
          if (suppressClickRef.current) return;
          // Deselect when clicking SVG background (not a shape)
          if (e.target === svgRef.current && !drawing && !current) {
            onSelectChange(null);
            if (multiSelectIds.size > 0) setMultiSelectIds(new Set());
          }
        }}
      >
        <g transform={`scale(${zoom})`}>
          {visibleMarkups.map((m) => {
            // Hyperlinks are suppressed on drafts (see hyperlinksVisible gate).
            if (m.type === 'hyperlink' && !hyperlinksVisible) return null;
            // Published markups are POSITION-LOCKED — drag/resize/vertex
            // handles disappear and the body becomes click-to-select
            // only. Other styling (color/fill/stroke/note) is still
            // editable via MarkupPopup; this lock is geometry-only so
            // canonical published artwork can't drift on the page.
            //
            // Select tool also disables piece-level pointer handlers so
            // that a previously-selected (or multi-selected) markup
            // doesn't intercept the marquee's first pointerdown via its
            // own move handler. Without this, the marquee never starts
            // when you drag from over an already-highlighted markup.
            const positionLocked = !!m.published;
            const inSelectMode   = activeTool === 'select';
            const editableThisMarkup = canEdit && !positionLocked && !inSelectMode;
            return (
              <React.Fragment key={m.id}>
                <MarkupShape
                  markup={m}
                  selected={selectedId === m.id || multiSelectIds.has(m.id)}
                  canEdit={editableThisMarkup}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (suppressClickRef.current) return;
                    if (m.type === 'hyperlink') {
                      const targetId = m.data?.target_sheet_id;
                      if (targetId) {
                        // Open the target sheet in a new browser tab via a
                        // deep link. App.jsx reads `?sheet=<id>` on boot
                        // and navigates to that sheet. Keeps the current
                        // tab's context intact — useful when cross-
                        // referencing section cuts without losing your
                        // place.
                        setHoveredHyperlink(null);
                        window.open(`?sheet=${encodeURIComponent(targetId)}`, '_blank', 'noopener,noreferrer');
                      }
                      return;
                    }
                    // Single-clicking a markup collapses any active
                    // multi-selection back to single-select on this one.
                    if (multiSelectIds.size > 0) setMultiSelectIds(new Set());
                    onSelectChange(m.id === selectedId ? null : m.id);
                  }}
                  onHyperlinkEnter={(e) => setHoveredHyperlink({ markup: m, clientX: e.clientX, clientY: e.clientY })}
                  onHyperlinkMove={(e) => setHoveredHyperlink((p) => p && p.markup.id === m.id ? { ...p, clientX: e.clientX, clientY: e.clientY } : p)}
                  onHyperlinkLeave={() => setHoveredHyperlink((p) => (p && p.markup.id === m.id ? null : p))}
                  onStartEdit={(m.type === 'hyperlink' || positionLocked) ? null : (canEdit ? startEdit : null)}
                  calibration={calibration}
                />
                {/* Normal markups get selection handles; hyperlinks +
                    position-locked (published) markups don't. */}
                {m.type !== 'hyperlink' && selectedId === m.id && editableThisMarkup && (
                  <EditHandles markup={m} zoom={zoom} onStartEdit={startEdit} />
                )}
                {/* Persistent chain icon for any markup with a linked file.
                    Click opens the in-app FileViewer; hover shows a
                    preview card. Sized inverse-to-zoom so it stays
                    legible at any zoom level. */}
                {m.linked_file && m.type !== 'hyperlink' && (
                  <LinkChainBadge
                    markup={m}
                    zoom={zoom}
                    kind="file"
                    onOpen={() => { setHoveredLink(null); setViewingFile(m.linked_file); }}
                    onEnter={(e) => setHoveredLink({ markup: m, clientX: e.clientX, clientY: e.clientY })}
                    onMove={(e) => setHoveredLink((p) => p && p.markup.id === m.id ? { ...p, clientX: e.clientX, clientY: e.clientY } : p)}
                    onLeave={() => setHoveredLink((p) => p && p.markup.id === m.id ? null : p)}
                  />
                )}
                {/* Sheet-link badge stacks just-left-of the file badge
                    when both are present (offsetIndex = 1). Click opens
                    the target sheet in a new tab via the same
                    `?sheet=<id>` deep link the hyperlink markups use,
                    so cross-referencing doesn't lose this tab's place. */}
                {m.linked_sheet && m.type !== 'hyperlink' && (
                  <LinkChainBadge
                    markup={m}
                    zoom={zoom}
                    kind="sheet"
                    offsetIndex={m.linked_file ? 1 : 0}
                    onOpen={() => {
                      setHoveredSheetLink(null);
                      window.open(`?sheet=${encodeURIComponent(m.linked_sheet.id)}`, '_blank', 'noopener,noreferrer');
                    }}
                    onEnter={(e) => setHoveredSheetLink({ markup: m, clientX: e.clientX, clientY: e.clientY })}
                    onMove={(e) => setHoveredSheetLink((p) => p && p.markup.id === m.id ? { ...p, clientX: e.clientX, clientY: e.clientY } : p)}
                    onLeave={() => setHoveredSheetLink((p) => p && p.markup.id === m.id ? null : p)}
                  />
                )}
              </React.Fragment>
            );
          })}
          {current && (
            <MarkupShape
              markup={{
                ...current,
                // cloud-callout draws as a plain cloud during the drag —
                // MarkupShape doesn't have a case for 'cloud-callout', and
                // the leader/text box don't appear until pointerup.
                type: current.type === 'cloud-callout' ? 'cloud' : current.type,
                data: shapeToData(current),
              }}
              live
              calibration={calibration}
            />
          )}

          {/* Cloud-callout text-edit preview: between pointerup of the cloud
              drag and submit of the text input, render the captured cloud
              + a placeholder text box + leader so the user sees what the
              final markup will look like. */}
          {cloudCalloutDraft && textInput && (() => {
            const W = 140, H = 40;
            return (
              <MarkupShape
                live
                calibration={calibration}
                markup={{
                  type: 'callout',
                  color:        cloudCalloutDraft.color,
                  fill_color:   cloudCalloutDraft.fill_color,
                  fill_opacity: cloudCalloutDraft.fill_opacity ?? 1,
                  stroke_width: cloudCalloutDraft.stroke_width ?? 2,
                  data: {
                    kind: 'cloud',
                    cloud: cloudCalloutDraft.cloud,
                    // textInput is the box top-left for the callout
                    // variant of TextInputOverlay; box origin must
                    // match so the leader points at the right place
                    // and the textarea stacks exactly over the box.
                    box: {
                      x: textInput.x,
                      y: textInput.y,
                      w: W, h: H,
                    },
                    text: '',
                    fontSize: 14,
                  },
                }}
              />
            );
          })()}

          {/* Plain-text drag-out preview: between pointerup of the text-box
              drag and submit of the text input, render the captured box so
              the user sees the eventual text-on-page surface while typing.
              The textarea (callout variant) stacks directly over this rect. */}
          {textDraft && textInput && (
            <MarkupShape
              live
              calibration={calibration}
              markup={{
                type: 'text',
                color:        textDraft.color,
                fill_color:   textDraft.fill_color,
                fill_opacity: textDraft.fill_opacity ?? 1,
                stroke_width: textDraft.stroke_width ?? 2,
                data: {
                  x: textDraft.box.x, y: textDraft.box.y,
                  w: textDraft.box.w, h: textDraft.box.h,
                  text: '', fontSize: 14,
                },
              }}
            />
          )}

          {/* Regular callout one-click preview: between the click and submit,
              render the box (at textInput) + leader to the click point + an
              arrowhead — same as the persisted markup, just with empty text
              while the user types. */}
          {calloutDraft && textInput && (() => {
            const W = 140, H = 40;
            return (
              <MarkupShape
                live
                calibration={calibration}
                markup={{
                  type: 'callout',
                  color:        calloutDraft.color,
                  fill_color:   calloutDraft.fill_color,
                  fill_opacity: calloutDraft.fill_opacity ?? 1,
                  stroke_width: calloutDraft.stroke_width ?? 2,
                  data: {
                    kind: 'rect',
                    box: {
                      x: textInput.x,
                      y: textInput.y,
                      w: W, h: H,
                    },
                    leader: calloutDraft.leader,
                    text: '',
                    fontSize: 14,
                  },
                }}
              />
            );
          })()}

          {/* Marquee selection rectangle — dashed, semi-transparent fill,
              non-interactive so the underlying markups still receive
              click/hover events. Drawn in page-point space inside the
              zoomed <g> so the dashes stay visually consistent. */}
          {marquee && (() => {
            const [mx, my, mw, mh] = normalizeRect(marquee.x, marquee.y, marquee.w, marquee.h);
            return (
              <g pointerEvents="none">
                <rect
                  x={mx} y={my} width={mw} height={mh}
                  fill="rgba(74, 158, 255, 0.10)"
                  stroke="var(--accent, #4a9eff)"
                  strokeWidth={1.2 / zoom}
                  strokeDasharray={`${4 / zoom} ${3 / zoom}`}
                />
              </g>
            );
          })()}

          {/* Multi-click rubber-band preview: dashed segment from last vertex to cursor.
              For polygons, also show a dashed close-segment back to the first vertex. */}
          {isMultiClickInProgress(current) && cursorPos && (() => {
            const pts = current.data.points;
            const last = pts[pts.length - 1];
            const first = pts[0];
            const isPolygon = current.data.kind === 'polygon';
            return (
              <g pointerEvents="none">
                <line
                  x1={last.x} y1={last.y}
                  x2={cursorPos.x} y2={cursorPos.y}
                  stroke={current.color || '#e8a020'}
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                  opacity={0.6}
                />
                {isPolygon && pts.length >= 2 && (
                  <line
                    x1={cursorPos.x} y1={cursorPos.y}
                    x2={first.x} y2={first.y}
                    stroke={current.color || '#e8a020'}
                    strokeWidth={1.5}
                    strokeDasharray="3 4"
                    opacity={0.35}
                  />
                )}
              </g>
            );
          })()}

          {/* Calibration pick markers — drawn in page-point space like markups */}
          {isCalibrating && calibrationPickPoints.map((p, i) => (
            <g key={`cal-${i}`} pointerEvents="none">
              <circle cx={p.x} cy={p.y} r={6 / zoom} fill="#e8a020" stroke="#111113" strokeWidth={1.5 / zoom}/>
              <text
                x={p.x + 10 / zoom}
                y={p.y - 8 / zoom}
                fill="#e8a020"
                fontSize={11 / zoom}
                fontFamily="IBM Plex Mono, monospace"
                fontWeight="600"
              >
                {i + 1}
              </text>
            </g>
          ))}
          {isCalibrating && calibrationPickPoints.length === 1 && (
            <line
              pointerEvents="none"
              x1={calibrationPickPoints[0].x}
              y1={calibrationPickPoints[0].y}
              x2={calibrationPickPoints[0].x}
              y2={calibrationPickPoints[0].y}
              stroke="#e8a020"
              strokeWidth={1.5 / zoom}
              strokeDasharray={`${4 / zoom} ${3 / zoom}`}
            />
          )}
        </g>
      </svg>

      {textInput && (() => {
        // Pick the active draft (at most one is set at a time). Each carries
        // the color picked at pointerdown so the textarea border matches.
        const draft = cloudCalloutDraft || textDraft || calloutDraft;
        // Box dimensions for the callout-variant textarea: textDraft has
        // the actual dragged size; cloud-callout / regular callout use a
        // fixed 140×40. Falls back to the plain (uncapped) variant when
        // no draft is set (legacy click-to-place flow, if ever reached).
        const variantProps = !draft ? {} : (textDraft ? {
          variant:  'callout',
          width:    textDraft.box.w * zoom,
          height:   textDraft.box.h * zoom,
          fontSize: 14 * zoom,
        } : {
          variant:  'callout',
          width:    140 * zoom,
          height:   40 * zoom,
          fontSize: 14 * zoom,
        });
        return (
          <TextInputOverlay
            x={textInput.x * zoom}
            y={textInput.y * zoom}
            color={draft ? (draft.color || activeColor) : activeColor}
            onSubmit={submitText}
            // Escape cancels the text input AND clears every draft so no
            // partial markup (cloud, leader, captured box) lingers on the
            // canvas.
            onCancel={() => {
              setTextInput(null);
              setCloudCalloutDraft(null);
              setTextDraft(null);
              setCalloutDraft(null);
            }}
            {...variantProps}
          />
        );
      })()}

      {hoveredHyperlink && <HyperlinkPreview hovered={hoveredHyperlink} sheets={sheets} />}

      {hoveredSheetLink && (
        <LinkedSheetPreview
          hovered={hoveredSheetLink}
          onOpen={() => {
            const id = hoveredSheetLink.markup.linked_sheet?.id;
            setHoveredSheetLink(null);
            if (id) window.open(`?sheet=${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
          }}
        />
      )}

      {hoveredLink && (
        <LinkedFilePreview
          hovered={hoveredLink}
          onOpen={() => { setHoveredLink(null); setViewingFile(hoveredLink.markup.linked_file); }}
        />
      )}

      {viewingFile && (
        <FileViewer
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  );
}

// ── MarkupShape ──
function MarkupShape({ markup, selected, canEdit, onClick, onStartEdit, live, calibration,
                      onHyperlinkEnter, onHyperlinkMove, onHyperlinkLeave }) {
  const d           = markup.data || {};
  const color       = markup.color || '#e8a020';
  const fill        = markup.fill_color || 'none';
  const fillOpacity = markup.fill_opacity ?? 1;
  const strokeW     = markup.stroke_width ?? 2;
  // Selection outline color — accent blue at full opacity. Was a 70%
  // translucent white, which disappeared against the typical white-PDF
  // page background and made single-select / multi-select effectively
  // invisible (the user only "knew" a markup was selected because the
  // single-select popup popped up). Multi-select has no popup, so the
  // outline IS the only feedback — needs to be high-contrast against
  // both light and dark backgrounds.
  const selColor    = 'var(--accent, #4a9eff)';
  const canMove     = !live && selected && canEdit && !!onStartEdit;

  const commonProps = {
    onClick,
    onPointerDown: canMove ? (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      e.stopPropagation();
      onStartEdit(markup, 'move', e.clientX, e.clientY);
    } : undefined,
    // Also swallow mousedown so the parent viewer's pan handler doesn't fire.
    // (mousedown is a separate event from pointerdown and propagates independently.)
    onMouseDown: canMove ? (e) => { e.stopPropagation(); } : undefined,
    style: { cursor: live ? 'default' : (canMove ? 'move' : 'pointer') },
  };

  switch (markup.type) {
    case 'arrow': {
      const arrowSize = d.arrowSize ?? 10;
      const dx = d.x2 - d.x1, dy = d.y2 - d.y1;
      const len = Math.sqrt(dx*dx + dy*dy) || 1;
      const ux = dx/len, uy = dy/len;
      const ax = d.x2 - ux*arrowSize - uy*(arrowSize/2);
      const ay = d.y2 - uy*arrowSize + ux*(arrowSize/2);
      const bx = d.x2 - ux*arrowSize + uy*(arrowSize/2);
      const by = d.y2 - uy*arrowSize - ux*(arrowSize/2);
      return (
        <g {...commonProps}>
          <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
            stroke={color} strokeWidth={strokeW} strokeLinecap="round"/>
          <polygon points={`${d.x2},${d.y2} ${ax},${ay} ${bx},${by}`} fill={color}/>
          {selected && <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
            stroke={selColor} strokeWidth={strokeW+2} strokeLinecap="round" strokeDasharray="4 3"/>}
        </g>
      );
    }
    case 'line':
      return (
        <g {...commonProps}>
          <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
            stroke={color} strokeWidth={strokeW} strokeLinecap="round"/>
          {selected && <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
            stroke={selColor} strokeWidth={strokeW+2} strokeDasharray="4 3"/>}
        </g>
      );
    case 'rect': {
      const [rx, ry, rw, rh] = normalizeRect(d.x, d.y, d.w, d.h);
      return (
        <g {...commonProps}>
          <rect x={rx} y={ry} width={rw} height={rh}
            stroke={color} strokeWidth={strokeW} fill={fill} fillOpacity={fillOpacity} rx="2"/>
          {selected && <rect x={rx-2} y={ry-2} width={rw+4} height={rh+4}
            stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>}
        </g>
      );
    }
    case 'ellipse': {
      // Bounding-box ellipse — same x/y/w/h drag-out shape as rect, just
      // rendered as an SVG <ellipse>. Selection bbox is the rect bbox so
      // resize handles can grab the corners + sides like rect.
      const [rx, ry, rw, rh] = normalizeRect(d.x, d.y, d.w, d.h);
      const cx = rx + rw / 2, cy = ry + rh / 2;
      const erx = rw / 2,     ery = rh / 2;
      return (
        <g {...commonProps}>
          <ellipse cx={cx} cy={cy} rx={erx} ry={ery}
            stroke={color} strokeWidth={strokeW} fill={fill} fillOpacity={fillOpacity}/>
          {selected && <rect x={rx-2} y={ry-2} width={rw+4} height={rh+4}
            stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>}
        </g>
      );
    }
    case 'polygon': {
      // Click-to-add-vertex closed shape (non-measure sibling of
      // measure-area kind=polygon). Renders as a closed path with
      // optional fill; selection draws a dashed bbox like rect/cloud.
      const pts = d.points || [];
      if (pts.length < 2) return null;
      const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + (pts.length >= 3 ? ' Z' : '');
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      return (
        <g {...commonProps}>
          <path d={pathD}
            stroke={color} strokeWidth={strokeW}
            fill={fill} fillOpacity={fillOpacity}
            strokeLinejoin="round" strokeLinecap="round"/>
          {selected && (
            <rect x={minX-3} y={minY-3} width={(maxX-minX)+6} height={(maxY-minY)+6}
              stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>
          )}
        </g>
      );
    }
    case 'cloud': {
      const [cx, cy, cw, ch] = normalizeRect(d.x, d.y, d.w, d.h);
      return (
        <g {...commonProps}>
          <path d={cloudPath(cx, cy, cw, ch, strokeW)}
            stroke={color} strokeWidth={strokeW} fill={fill} fillOpacity={fillOpacity}/>
          {selected && <rect x={cx-3} y={cy-3} width={cw+6} height={ch+6}
            stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>}
        </g>
      );
    }
    case 'text': {
      const fontSize = d.fontSize ?? 14;
      // Boxed text (drag-out flow): data has w/h. Render a translucent-
      // white background rect with the text wrapped + centered inside,
      // matching the callout text box look. The bg is drawn even if the
      // user didn't pick a fill color so it stays legible over the page.
      if (d.w != null && d.h != null) {
        const [bx, by, bw, bh] = normalizeRect(d.x, d.y, d.w, d.h);
        const bgFill = (fill && fill !== 'transparent')
          ? fill
          : 'rgba(255, 255, 255, 0.92)';
        const bgOpacity = (fill && fill !== 'transparent') ? fillOpacity : 1;
        const lines = wrapTextToWidth(d.text || '', bw - 12, fontSize);
        const totalH = lines.length * fontSize * 1.2;
        const startY = by + (bh - totalH) / 2 + fontSize;
        return (
          <g {...commonProps}>
            <rect x={bx} y={by} width={bw} height={bh} rx="3"
              stroke={color} strokeWidth={strokeW}
              fill={bgFill} fillOpacity={bgOpacity}/>
            {lines.map((line, i) => (
              <text
                key={i}
                x={bx + bw / 2}
                y={startY + i * fontSize * 1.2}
                fill={color}
                fontSize={fontSize}
                fontFamily="IBM Plex Mono, monospace"
                fontWeight="500"
                textAnchor="middle"
                pointerEvents="none"
              >{line}</text>
            ))}
            {selected && <rect x={bx-2} y={by-2} width={bw+4} height={bh+4}
              stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"
              pointerEvents="none"/>}
          </g>
        );
      }
      // Legacy unboxed text (click-to-place / pre-drag-out flow).
      return (
        <g {...commonProps}>
          <text x={d.x} y={d.y}
            fill={color}
            fontSize={fontSize}
            fontFamily="IBM Plex Mono, monospace"
            fontWeight="500"
          >
            {d.text}
          </text>
          {selected && (
            <rect x={d.x - 2} y={d.y - fontSize} width={(d.text||'').length * (fontSize * 0.6) + 4} height={fontSize + 4}
              stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="3 2"/>
          )}
        </g>
      );
    }
    case 'callout': {
      // Two distinct geometries — see submitText for creation defaults.
      //   rect  : data = { kind, box, leader:{x,y}, text }
      //           Renders text box + leader line + arrowhead at leader endpoint.
      //   cloud : data = { kind, box, cloud:{x,y,w,h}, text }
      //           Renders text box + leader from box edge to cloud edge +
      //           cloud-shaped path around `cloud`. No arrowhead.
      const kind = d.kind || 'rect';
      const fontSize = d.fontSize ?? 14;
      const box = d.box || { x: 0, y: 0, w: 140, h: 40 };
      const [bx, by, bw, bh] = normalizeRect(box.x, box.y, box.w, box.h);

      // Background of the text box — translucent white by default so text
      // reads over the drawing; user-picked fill color overrides.
      const bgFill = (fill && fill !== 'transparent')
        ? fill
        : 'rgba(255, 255, 255, 0.92)';
      const bgOpacity = (fill && fill !== 'transparent') ? fillOpacity : 1;

      const lines = wrapTextToWidth(d.text || '', bw - 12, fontSize);

      // Compute leader geometry per kind.
      let leaderStart, leaderEnd, arrowPts = '', cloudRect = null;
      if (kind === 'rect') {
        const leader = d.leader || { x: bx + bw + 60, y: by - 40 };
        leaderStart = boxLeaderAttachment({ x: bx, y: by, w: bw, h: bh }, leader);
        leaderEnd   = leader;
        // Arrowhead at endpoint, oriented along the leader.
        const ahSize = 10;
        const lx = leaderEnd.x - leaderStart.x;
        const ly = leaderEnd.y - leaderStart.y;
        const llen = Math.sqrt(lx*lx + ly*ly) || 1;
        const ux = lx / llen, uy = ly / llen;
        const ax = leaderEnd.x - ux*ahSize - uy*(ahSize/2);
        const ay = leaderEnd.y - uy*ahSize + ux*(ahSize/2);
        const cx2 = leaderEnd.x - ux*ahSize + uy*(ahSize/2);
        const cy2 = leaderEnd.y - uy*ahSize - ux*(ahSize/2);
        arrowPts = `${leaderEnd.x},${leaderEnd.y} ${ax},${ay} ${cx2},${cy2}`;
      } else {
        // cloud kind: leader connects nearest box edge → nearest cloud edge.
        const cloud = d.cloud || { x: bx + bw + 80, y: by + 30, w: 120, h: 80 };
        const [cx, cy, cw, ch] = normalizeRect(cloud.x, cloud.y, cloud.w, cloud.h);
        cloudRect = { x: cx, y: cy, w: cw, h: ch };
        const cloudCenter = { x: cx + cw / 2, y: cy + ch / 2 };
        const boxCenter   = { x: bx + bw / 2, y: by + bh / 2 };
        leaderStart = boxLeaderAttachment({ x: bx, y: by, w: bw, h: bh }, cloudCenter);
        leaderEnd   = boxLeaderAttachment({ x: cx, y: cy, w: cw, h: ch }, boxCenter);
      }

      // Per-piece pointer handlers, attached directly to the visible shape
      // (rect for the box, path / hit-rect for the cloud). Putting these on
      // a wrapping <g> proved unreliable for cursor cascade and click
      // routing on iPad — the actual hit element is what owns the cursor +
      // the drag start.
      //
      // Each piece is also a click target for selection: if the markup
      // isn't selected yet, the click selects it without starting a drag;
      // once selected, the same area becomes draggable.
      const pieceProps = (mode) => {
        if (!canEdit || !onStartEdit) return { style: { cursor: 'pointer' } };
        if (canMove) {
          return {
            onPointerDown: (e) => {
              if (e.button !== 0 && e.pointerType === 'mouse') return;
              e.stopPropagation();
              onStartEdit(markup, mode, e.clientX, e.clientY);
            },
            onMouseDown: (e) => e.stopPropagation(),
            onClick: commonProps.onClick,
            style: { cursor: 'move' },
          };
        }
        // Not yet selected — clicking the piece selects the markup; cursor
        // hints that it's interactive.
        return {
          onClick: commonProps.onClick,
          style: { cursor: 'pointer' },
        };
      };

      // For rect kind, body drag = move whole callout (legacy behavior).
      // For cloud kind, the outer group has only the click — each piece
      // (box / cloud) installs its own pointer handlers.
      const outerProps = (kind === 'rect') ? commonProps : {
        // Don't propagate clicks/drags from the outer group: pieces own
        // their own routing now. Without this, an outer onClick would
        // double-toggle selection on top of the piece's onClick.
        style: { cursor: 'default' },
      };

      return (
        <g {...outerProps}>
          {/* Leader (drawn first so the pieces sit on top of its tail) */}
          <line
            x1={leaderStart.x} y1={leaderStart.y}
            x2={leaderEnd.x}   y2={leaderEnd.y}
            stroke={color} strokeWidth={strokeW} strokeLinecap="round"
            pointerEvents="none"
          />
          {kind === 'rect' && arrowPts && (
            <polygon points={arrowPts} fill={color} />
          )}

          {/* Leader hit-line: invisible thick stroke at the same coords so
              hovering anywhere near the leader gives a forgiving target.
              Drag = move the whole markup (box + cloud/leader together).
              `butt` linecaps (the default) so the hit area doesn't poke
              past leaderStart/leaderEnd into the box / cloud — round caps
              would steal hover events near those edges. */}
          <line
            x1={leaderStart.x} y1={leaderStart.y}
            x2={leaderEnd.x}   y2={leaderEnd.y}
            stroke="transparent"
            strokeWidth={Math.max(strokeW * 4, 12)}
            strokeLinecap="butt"
            pointerEvents="stroke"
            {...pieceProps('move')}
          />

          {/* Cloud (cloud kind only) — interior + outline both have to be
              draggable. We render an invisible hit-rect over the cloud's
              bbox FIRST (so it's beneath the wavy stroke visually but
              earlier in DOM), then the wavy path on top. Both share the
              same pieceProps so either area triggers cloud-move. */}
          {kind === 'cloud' && cloudRect && (
            <>
              <rect
                x={cloudRect.x} y={cloudRect.y}
                width={cloudRect.w} height={cloudRect.h}
                fill="transparent"
                pointerEvents="all"
                {...pieceProps('cloud-move')}
              />
              <path
                d={cloudPath(cloudRect.x, cloudRect.y, cloudRect.w, cloudRect.h, strokeW)}
                stroke={color} strokeWidth={strokeW}
                fill="none"
                pointerEvents="all"
                {...pieceProps('cloud-move')}
              />
            </>
          )}

          {/* Text box body — fully inline so we don't lose anything to a
              spread. Setting cursor BOTH as SVG attribute AND as inline
              style so whichever path the browser respects, we win. Drag
              always uses 'box-move' so the leader endpoint (arrow tip
              for rect kind, cloud anchor for cloud kind) stays pinned
              while the box repositions — the leader line auto-rotates
              from the new box edge to the fixed endpoint. To translate
              the whole markup, drag the leader line instead. */}
          <rect
            x={bx} y={by} width={bw} height={bh} rx="3"
            stroke={color} strokeWidth={strokeW}
            fill={bgFill} fillOpacity={bgOpacity}
            pointerEvents="all"
            cursor={canMove ? 'move' : (canEdit ? 'pointer' : 'default')}
            style={{ cursor: canMove ? 'move' : (canEdit ? 'pointer' : 'default') }}
            onPointerDown={canMove && onStartEdit ? (e) => {
              if (e.button !== 0 && e.pointerType === 'mouse') return;
              e.stopPropagation();
              onStartEdit(markup, 'box-move', e.clientX, e.clientY);
            } : undefined}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClick}
          />
          {lines.map((line, i) => {
            const totalH = lines.length * fontSize * 1.2;
            const startY = by + (bh - totalH) / 2 + fontSize;
            return (
              <text
                key={i}
                x={bx + bw / 2}
                y={startY + i * fontSize * 1.2}
                fill={color}
                fontSize={fontSize}
                fontFamily="IBM Plex Mono, monospace"
                fontWeight="500"
                textAnchor="middle"
                pointerEvents="none"
              >{line}</text>
            );
          })}

          {selected && (
            <>
              {/* Selection outlines are purely visual — pointerEvents='none'
                  so they don't sit on top of the box / cloud and steal
                  hover events. (Without this, fill='none' + the rect's
                  bounding box can register elementFromPoint hits across
                  the whole interior in some browsers, leaving the user
                  with the parent <g>'s 'default' cursor instead of the
                  piece's 'move'.) */}
              <rect
                x={bx - 3} y={by - 3} width={bw + 6} height={bh + 6} rx="4"
                stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3"
                pointerEvents="none"
              />
              {cloudRect && (
                <rect
                  x={cloudRect.x - 3} y={cloudRect.y - 3}
                  width={cloudRect.w + 6} height={cloudRect.h + 6}
                  stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="4"
                  pointerEvents="none"
                />
              )}
            </>
          )}
        </g>
      );
    }
    case 'freehand': {
      const pts = d.points || [];
      if (pts.length < 2) return null;
      const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      return (
        <g {...commonProps}>
          <path d={pathD} stroke={color} strokeWidth={strokeW} fill="none"
            strokeLinecap="round" strokeLinejoin="round"/>
          {selected && (
            <rect x={minX-3} y={minY-3} width={(maxX-minX)+6} height={(maxY-minY)+6}
              stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>
          )}
        </g>
      );
    }
    case 'polyline': {
      // Click-to-add-vertex regular line markup. No measurement label
      // (that's measure-length kind=polyline). Points connect with
      // straight segments; selection draws a dashed bbox like freehand.
      const pts = d.points || [];
      if (pts.length < 2) return null;
      const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      return (
        <g {...commonProps}>
          <path d={pathD} stroke={color} strokeWidth={strokeW} fill="none"
            strokeLinecap="round" strokeLinejoin="round"/>
          {selected && (
            <rect x={minX-3} y={minY-3} width={(maxX-minX)+6} height={(maxY-minY)+6}
              stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>
          )}
        </g>
      );
    }
    case 'measure': {
      const dx = d.x2 - d.x1, dy = d.y2 - d.y1;
      const len = Math.sqrt(dx*dx + dy*dy);
      const label = formatLength(len, calibration);
      const mx = (d.x1 + d.x2) / 2;
      const my = (d.y1 + d.y2) / 2;
      return (
        <g {...commonProps}>
          <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
            stroke={color} strokeWidth={strokeW} strokeLinecap="round" strokeDasharray="5 3"/>
          <line x1={d.x1} y1={d.y1-6} x2={d.x1} y2={d.y1+6} stroke={color} strokeWidth={strokeW}/>
          <line x1={d.x2} y1={d.y2-6} x2={d.x2} y2={d.y2+6} stroke={color} strokeWidth={strokeW}/>
          <rect x={mx - label.length*3.5} y={my - 9} width={label.length*7} height={14}
            fill="var(--bg)" opacity="0.85" rx="2"/>
          <text x={mx} y={my + 3}
            fill={color} fontSize="10" fontFamily="IBM Plex Mono, monospace"
            textAnchor="middle" fontWeight="600"
          >{label}</text>
        </g>
      );
    }
    case 'measure-length': {
      const kind = d.kind || 'line';
      if (kind === 'line') {
        const dx = d.x2 - d.x1, dy = d.y2 - d.y1;
        const len = Math.sqrt(dx*dx + dy*dy);
        const label = formatLength(len, calibration);
        const mx = (d.x1 + d.x2) / 2;
        const my = (d.y1 + d.y2) / 2;
        return (
          <g {...commonProps}>
            <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
              stroke={color} strokeWidth={strokeW} strokeLinecap="round" strokeDasharray="5 3"/>
            <line x1={d.x1} y1={d.y1-6} x2={d.x1} y2={d.y1+6} stroke={color} strokeWidth={strokeW}/>
            <line x1={d.x2} y1={d.y2-6} x2={d.x2} y2={d.y2+6} stroke={color} strokeWidth={strokeW}/>
            <MeasureLabel x={mx} y={my} text={label} color={color} />
            {selected && <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
              stroke={selColor} strokeWidth={strokeW+2} strokeLinecap="round" strokeDasharray="4 3"/>}
          </g>
        );
      }
      if (kind === 'polyline') {
        const pts = d.points || [];
        if (pts.length < 2) return null;
        const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
        const total = polylineLength(pts);
        const segLabels = [];
        for (let i = 1; i < pts.length; i++) {
          const seg = distance(pts[i-1], pts[i]);
          const sMx = (pts[i-1].x + pts[i].x) / 2;
          const sMy = (pts[i-1].y + pts[i].y) / 2;
          segLabels.push({ x: sMx, y: sMy, text: formatLength(seg, calibration) });
        }
        // Total label sits just past the final vertex along the last segment.
        const lastA = pts[pts.length - 2];
        const lastB = pts[pts.length - 1];
        const lvx = lastB.x - lastA.x, lvy = lastB.y - lastA.y;
        const lvLen = Math.sqrt(lvx*lvx + lvy*lvy) || 1;
        const ox = lastB.x + (lvx / lvLen) * 14;
        const oy = lastB.y + (lvy / lvLen) * 14;
        return (
          <g {...commonProps}>
            <path d={pathD} stroke={color} strokeWidth={strokeW}
              fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            {pts.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={2.5} fill={color}/>
            ))}
            {segLabels.map((l, i) => (
              <MeasureLabel key={i} x={l.x} y={l.y} text={l.text} color={color} muted />
            ))}
            <MeasureLabel x={ox} y={oy} text={`Σ ${formatLength(total, calibration)}`} color={color} bold />
            {selected && (
              <path d={pathD} stroke={selColor} strokeWidth={strokeW+2}
                fill="none" strokeDasharray="4 3" strokeLinecap="round" strokeLinejoin="round"/>
            )}
          </g>
        );
      }
      if (kind === 'freeline') {
        const pts = d.points || [];
        if (pts.length < 2) return null;
        const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
        const total = polylineLength(pts);
        // Label centered on the path midpoint (approx — by cumulative length).
        const mid = pathMidpoint(pts);
        const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        return (
          <g {...commonProps}>
            <path d={pathD} stroke={color} strokeWidth={strokeW}
              fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            <MeasureLabel x={mid.x} y={mid.y} text={formatLength(total, calibration)} color={color} bold />
            {selected && (
              <rect x={minX-3} y={minY-3} width={(maxX-minX)+6} height={(maxY-minY)+6}
                stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>
            )}
          </g>
        );
      }
      return null;
    }
    case 'measure-area':
    case 'measure-volume': {
      return renderAreaOrVolume({
        markup, d, color, fill, fillOpacity, strokeW, selColor, selected,
        commonProps, calibration,
      });
    }
    case 'hyperlink': {
      // Highlighter-style overlay over a detected callout bubble — like
      // a soft light-blue fill that reads as "this text is linked",
      // similar to Acrobat's link highlighting. Stays attached to the
      // underlying text because the coords are in page-point space and
      // the outer <g> is transformed with the zoom.
      const x = d.x || 0, y = d.y || 0, w = d.w || 0, h = d.h || 0;
      if (w <= 0 || h <= 0) return null;
      return (
        <g
          onClick={onClick}
          onPointerDown={(e) => {
            // Swallow so drawing tools don't start a shape, and so the
            // viewer's pan handler doesn't kick in on left-click.
            if (e.button === 0) e.stopPropagation();
          }}
          onMouseDown={(e) => { if (e.button === 0) e.stopPropagation(); }}
          onMouseEnter={onHyperlinkEnter}
          onMouseMove={onHyperlinkMove}
          onMouseLeave={onHyperlinkLeave}
          style={{ cursor: 'pointer' }}
          className="cf-hyperlink"
        >
          <rect
            x={x} y={y} width={w} height={h}
            fill="#93c5fd"          /* very light blue (tailwind blue-300) */
            fillOpacity={0.35}
            stroke="none"
            pointerEvents="all"
          />
        </g>
      );
    }
    default: return null;
  }
}

// Shared renderer for `measure-area` and `measure-volume`. They only differ
// in label text (sq ft vs cu ft); the shape & outline are identical.
function renderAreaOrVolume({ markup, d, color, fill, fillOpacity, strokeW, selColor, selected, commonProps, calibration }) {
  const kind = d.kind || 'rect';
  // Compute area (page-points²) and the anchor point for the label.
  let pagePtsSq = 0;
  let centroid = { x: 0, y: 0 };
  let outline = null; // SVG element for the shape itself
  let bbox = null;    // { x, y, w, h } in normalized space for selection highlight

  if (kind === 'rect') {
    const [rx, ry, rw, rh] = normalizeRect(d.x, d.y, d.w, d.h);
    pagePtsSq = rw * rh;
    centroid = { x: rx + rw/2, y: ry + rh/2 };
    outline = (
      <rect x={rx} y={ry} width={rw} height={rh}
        stroke={color} strokeWidth={strokeW}
        fill={fill} fillOpacity={fillOpacity * 0.4}
        rx="2"
      />
    );
    bbox = { x: rx, y: ry, w: rw, h: rh };
  } else if (kind === 'polygon' || kind === 'freeform') {
    const pts = d.points || [];
    if (pts.length < 3) return null;
    pagePtsSq = polygonArea(pts);
    centroid = polygonCentroid(pts);
    const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';
    outline = (
      <>
        <path d={pathD} stroke={color} strokeWidth={strokeW}
          fill={fill} fillOpacity={fillOpacity * 0.4}
          strokeLinejoin="round"
        />
        {kind === 'polygon' && pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={2.5} fill={color}/>
        ))}
      </>
    );
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    bbox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  } else {
    return null;
  }

  // Label — area for measure-area, volume (using depthFt) for measure-volume.
  let labelText;
  if (markup.type === 'measure-volume') {
    const depthFt = Number(d.depthFt);
    const depthOk = Number.isFinite(depthFt) && depthFt > 0;
    labelText = depthOk
      ? formatVolume(pagePtsSq, depthFt, calibration)
      : `${formatArea(pagePtsSq, calibration)} · set depth`;
  } else {
    labelText = formatArea(pagePtsSq, calibration);
  }

  return (
    <g {...commonProps}>
      {outline}
      <MeasureLabel x={centroid.x} y={centroid.y} text={labelText} color={color} bold />
      {selected && bbox && (
        <rect x={bbox.x - 3} y={bbox.y - 3} width={bbox.w + 6} height={bbox.h + 6}
          stroke={selColor} strokeWidth={1.6} fill="none" strokeDasharray="4 3" rx="3"/>
      )}
    </g>
  );
}

// Reusable pill-style label for measurement readouts.
function MeasureLabel({ x, y, text, color, bold, muted }) {
  const fontSize = muted ? 9 : 10;
  const padX = 3;
  const w = text.length * (fontSize * 0.62) + padX * 2;
  const h = fontSize + 5;
  return (
    <g pointerEvents="none">
      <rect
        x={x - w/2} y={y - h/2}
        width={w} height={h}
        fill="var(--bg, #0b0b0d)"
        opacity={muted ? 0.7 : 0.88}
        rx="2"
      />
      <text
        x={x} y={y + fontSize/2 - 1.5}
        fill={color}
        fontSize={fontSize}
        fontFamily="IBM Plex Mono, monospace"
        fontWeight={bold ? 700 : 500}
        textAnchor="middle"
        opacity={muted ? 0.85 : 1}
      >
        {text}
      </text>
    </g>
  );
}

// Approximate midpoint along a polyline (by cumulative arc length).
function pathMidpoint(pts) {
  if (!pts || pts.length === 0) return { x: 0, y: 0 };
  if (pts.length === 1) return pts[0];
  const total = polylineLength(pts);
  const half = total / 2;
  let cum = 0;
  for (let i = 1; i < pts.length; i++) {
    const seg = distance(pts[i-1], pts[i]);
    if (cum + seg >= half) {
      const t = (half - cum) / (seg || 1);
      return {
        x: pts[i-1].x + (pts[i].x - pts[i-1].x) * t,
        y: pts[i-1].y + (pts[i].y - pts[i-1].y) * t,
      };
    }
    cum += seg;
  }
  return pts[pts.length - 1];
}

// ── EditHandles ──
function EditHandles({ markup, zoom, onStartEdit }) {
  const d  = markup.data || {};
  const r  = 6 / zoom;
  const sw = 1.5 / zoom;

  function Handle({ mode, cx, cy, cursor }) {
    return (
      <circle
        cx={cx} cy={cy} r={r}
        fill="#ffffff" stroke="#111113" strokeWidth={sw}
        style={{ cursor }}
        onPointerDown={(e) => {
          if (e.button !== 0 && e.pointerType === 'mouse') return;
          e.stopPropagation();
          onStartEdit(markup, mode, e.clientX, e.clientY);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  switch (markup.type) {
    case 'arrow':
    case 'line':
    case 'measure':
      return (
        <>
          <Handle mode="resize-start" cx={d.x1} cy={d.y1} cursor="grab" />
          <Handle mode="resize-end"   cx={d.x2} cy={d.y2} cursor="grab" />
        </>
      );
    case 'measure-length': {
      if (d.kind === 'line') {
        return (
          <>
            <Handle mode="resize-start" cx={d.x1} cy={d.y1} cursor="grab" />
            <Handle mode="resize-end"   cx={d.x2} cy={d.y2} cursor="grab" />
          </>
        );
      }
      if (d.kind === 'polyline') {
        return (
          <>
            {(d.points || []).map((p, i) => (
              <Handle key={i} mode={`vertex-${i}`} cx={p.x} cy={p.y} cursor="grab" />
            ))}
          </>
        );
      }
      return null; // freeline: move-only
    }
    case 'measure-area':
    case 'measure-volume': {
      if (d.kind === 'rect') {
        const [nx, ny, nw, nh] = normalizeRect(d.x, d.y, d.w, d.h);
        const mx = nx + nw/2, my = ny + nh/2;
        return (
          <>
            <Handle mode="resize-nw" cx={nx}       cy={ny}       cursor="nwse-resize" />
            <Handle mode="resize-n"  cx={mx}       cy={ny}       cursor="ns-resize"   />
            <Handle mode="resize-ne" cx={nx+nw}    cy={ny}       cursor="nesw-resize" />
            <Handle mode="resize-e"  cx={nx+nw}    cy={my}       cursor="ew-resize"   />
            <Handle mode="resize-se" cx={nx+nw}    cy={ny+nh}    cursor="nwse-resize" />
            <Handle mode="resize-s"  cx={mx}       cy={ny+nh}    cursor="ns-resize"   />
            <Handle mode="resize-sw" cx={nx}       cy={ny+nh}    cursor="nesw-resize" />
            <Handle mode="resize-w"  cx={nx}       cy={my}       cursor="ew-resize"   />
          </>
        );
      }
      if (d.kind === 'polygon') {
        return (
          <>
            {(d.points || []).map((p, i) => (
              <Handle key={i} mode={`vertex-${i}`} cx={p.x} cy={p.y} cursor="grab" />
            ))}
          </>
        );
      }
      return null; // freeform: move-only
    }
    case 'rect':
    case 'ellipse':
    case 'cloud': {
      const [nx, ny, nw, nh] = normalizeRect(d.x, d.y, d.w, d.h);
      const mx = nx + nw/2, my = ny + nh/2;
      return (
        <>
          <Handle mode="resize-nw" cx={nx}       cy={ny}       cursor="nwse-resize" />
          <Handle mode="resize-n"  cx={mx}       cy={ny}       cursor="ns-resize"   />
          <Handle mode="resize-ne" cx={nx+nw}    cy={ny}       cursor="nesw-resize" />
          <Handle mode="resize-e"  cx={nx+nw}    cy={my}       cursor="ew-resize"   />
          <Handle mode="resize-se" cx={nx+nw}    cy={ny+nh}    cursor="nwse-resize" />
          <Handle mode="resize-s"  cx={mx}       cy={ny+nh}    cursor="ns-resize"   />
          <Handle mode="resize-sw" cx={nx}       cy={ny+nh}    cursor="nesw-resize" />
          <Handle mode="resize-w"  cx={nx}       cy={my}       cursor="ew-resize"   />
        </>
      );
    }
    case 'polygon':
      // Vertex handles, same pattern as measure-area kind=polygon —
      // each handle lets the user nudge a single vertex without
      // moving the whole shape.
      return (
        <>
          {(d.points || []).map((p, i) => (
            <Handle key={i} mode={`vertex-${i}`} cx={p.x} cy={p.y} cursor="grab" />
          ))}
        </>
      );
    case 'callout': {
      // Box always gets 8 resize handles. Then per kind:
      //   rect  → +1 leader endpoint handle (re-aim the arrow)
      //   cloud → +8 cloud resize handles (resize the area being called out)
      const kind = d.kind || 'rect';
      const box = d.box || { x: 0, y: 0, w: 140, h: 40 };
      const [nx, ny, nw, nh] = normalizeRect(box.x, box.y, box.w, box.h);
      const mx = nx + nw/2, my = ny + nh/2;
      const boxHandles = (
        <>
          <Handle mode="resize-nw" cx={nx}       cy={ny}       cursor="nwse-resize" />
          <Handle mode="resize-n"  cx={mx}       cy={ny}       cursor="ns-resize"   />
          <Handle mode="resize-ne" cx={nx+nw}    cy={ny}       cursor="nesw-resize" />
          <Handle mode="resize-e"  cx={nx+nw}    cy={my}       cursor="ew-resize"   />
          <Handle mode="resize-se" cx={nx+nw}    cy={ny+nh}    cursor="nwse-resize" />
          <Handle mode="resize-s"  cx={mx}       cy={ny+nh}    cursor="ns-resize"   />
          <Handle mode="resize-sw" cx={nx}       cy={ny+nh}    cursor="nesw-resize" />
          <Handle mode="resize-w"  cx={nx}       cy={my}       cursor="ew-resize"   />
        </>
      );
      if (kind === 'rect') {
        const leader = d.leader || { x: box.x + box.w + 60, y: box.y - 40 };
        return (
          <>
            {boxHandles}
            <Handle mode="leader" cx={leader.x} cy={leader.y} cursor="grab" />
          </>
        );
      }
      // cloud kind — also render 8 resize handles around the cloud rect.
      const cloud = d.cloud || { x: box.x + box.w + 80, y: box.y + 30, w: 120, h: 80 };
      const [cx, cy, cw, ch] = normalizeRect(cloud.x, cloud.y, cloud.w, cloud.h);
      const cmx = cx + cw/2, cmy = cy + ch/2;
      return (
        <>
          {boxHandles}
          <Handle mode="cloud-resize-nw" cx={cx}     cy={cy}     cursor="nwse-resize" />
          <Handle mode="cloud-resize-n"  cx={cmx}    cy={cy}     cursor="ns-resize"   />
          <Handle mode="cloud-resize-ne" cx={cx+cw}  cy={cy}     cursor="nesw-resize" />
          <Handle mode="cloud-resize-e"  cx={cx+cw}  cy={cmy}    cursor="ew-resize"   />
          <Handle mode="cloud-resize-se" cx={cx+cw}  cy={cy+ch}  cursor="nwse-resize" />
          <Handle mode="cloud-resize-s"  cx={cmx}    cy={cy+ch}  cursor="ns-resize"   />
          <Handle mode="cloud-resize-sw" cx={cx}     cy={cy+ch}  cursor="nesw-resize" />
          <Handle mode="cloud-resize-w"  cx={cx}     cy={cmy}    cursor="ew-resize"   />
        </>
      );
    }
    default:
      return null; // text / freehand: move-only
  }
}

// ── TextInputOverlay ──
function TextInputOverlay({ x, y, color, onSubmit, onCancel, variant = 'plain', width, height, fontSize }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(e.target.value); }
    if (e.key === 'Escape') onCancel();
  }
  // 'callout' variant: textarea is positioned + sized to match the eventual
  // callout's text box exactly so the user feels they're typing directly
  // into the box (no second floating input on top of the orange outline).
  // x,y are the box top-left; width/height/fontSize are CSS pixels at the
  // current zoom level (caller multiplies by zoom).
  const isCallout = variant === 'callout';
  const className = isCallout ? styles.calloutInput : styles.textInput;
  const inlineStyle = isCallout
    ? { left: x, top: y, width, height, borderColor: color, fontSize }
    : { left: x, top: y, color, borderColor: color };
  return (
    <textarea
      ref={inputRef}
      className={className}
      style={inlineStyle}
      rows={1}
      placeholder={isCallout ? 'Type, then Enter' : 'Type and press Enter…'}
      onKeyDown={onKeyDown}
      onBlur={(e) => onSubmit(e.target.value)}
    />
  );
}

// ── LinkChainBadge ──
// Small circle drawn at the markup's top-right corner. Sized inverse-to-
// zoom so it stays roughly the same visual size as the user zooms in/out.
// Click → onOpen; hover → onEnter / Move / Leave drives the floating
// preview card. `kind` = 'file' (chain glyph, blue) or 'sheet' (page
// glyph, green); `offsetIndex` shifts the badge left by index slots so
// when both kinds exist they stack horizontally instead of overlapping.
function LinkChainBadge({ markup, zoom, onOpen, onEnter, onMove, onLeave, kind = 'file', offsetIndex = 0 }) {
  const corner = markupTopRightCorner(markup);
  // Inverse-zoom sizing — keeps the badge ~14px regardless of zoom.
  const r  = 10 / zoom;
  const ix = 5 / zoom;
  const sw = 1.5 / zoom;
  // Shift each subsequent badge ~22 px (badge width + small gap) to the
  // left so they stack horizontally at top-right of the markup.
  const stagger = offsetIndex * (22 / zoom);
  const cx = corner.x + r * 0.7 - stagger;
  const cy = corner.y - r * 0.7;
  const fill = kind === 'sheet'
    ? 'var(--green, #3ecf8e)'
    : 'var(--accent, #4a9eff)';
  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={onEnter}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <circle cx={cx} cy={cy} r={r}
        fill={fill} stroke="#111113" strokeWidth={sw}
      />
      {kind === 'sheet' ? (
        // Folded-page glyph for sheet links — distinct from the chain
        // glyph below so the two badges read at a glance.
        <g transform={`translate(${cx - ix*0.7} ${cy - ix*0.85})`}>
          <path
            d={`M 0 0 L ${ix*0.95} 0 L ${ix*1.4} ${ix*0.45} L ${ix*1.4} ${ix*1.7} L 0 ${ix*1.7} Z
                M ${ix*0.95} 0 L ${ix*0.95} ${ix*0.45} L ${ix*1.4} ${ix*0.45}`}
            stroke="#fff" strokeWidth={Math.max(1, 1.2 / zoom)}
            fill="none" strokeLinecap="round" strokeLinejoin="round"
          />
        </g>
      ) : (
        // Chain-link glyph — two interlocking rounded rectangles, the
        // legacy "linked file" indicator.
        <g transform={`translate(${cx - ix} ${cy - ix})`}>
          <path
            d={`M ${ix*0.4} ${ix*0.9} L ${ix*0.9} ${ix*0.4}
                M ${ix*0.2} ${ix*1.4} a ${ix*0.45} ${ix*0.35} 0 0 1 0 -${ix*0.7}
                l ${ix*0.4} -${ix*0.4}
                M ${ix*1.4} ${ix*0.6} a ${ix*0.45} ${ix*0.35} 0 0 1 0 ${ix*0.7}
                l -${ix*0.4} ${ix*0.4}`}
            stroke="#fff" strokeWidth={Math.max(1, 1.2 / zoom)}
            fill="none" strokeLinecap="round" strokeLinejoin="round"
          />
        </g>
      )}
    </g>
  );
}

// Top-right corner of a markup's visual bounds, in page-point coords. Used
// to anchor the link-chain badge.
function markupTopRightCorner(m) {
  const d = m.data || {};
  switch (m.type) {
    case 'arrow': case 'line': case 'measure': {
      const x1 = d.x1 || 0, x2 = d.x2 || 0, y1 = d.y1 || 0, y2 = d.y2 || 0;
      return { x: Math.max(x1, x2), y: Math.min(y1, y2) };
    }
    case 'rect': case 'cloud': case 'ellipse': {
      const x = d.w < 0 ? d.x + d.w : d.x;
      const y = d.h < 0 ? d.y + d.h : d.y;
      return { x: x + Math.abs(d.w || 0), y };
    }
    case 'polyline':
    case 'polygon': {
      const pts = d.points || [];
      if (!pts.length) return { x: 0, y: 0 };
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      return { x: Math.max(...xs), y: Math.min(...ys) };
    }
    case 'text': {
      const fs = d.fontSize ?? 14;
      const w  = (d.text || '').length * (fs * 0.6);
      return { x: (d.x || 0) + w, y: (d.y || 0) - fs };
    }
    case 'callout': {
      // For cloud-callouts the "useful" corner is the cloud (the indicator),
      // not the text box. Fall back to the box if there's no cloud yet.
      const target = m.data?.kind === 'cloud' && m.data?.cloud ? m.data.cloud : (m.data?.box || { x:0, y:0, w:140, h:40 });
      const x = target.w < 0 ? target.x + target.w : target.x;
      const y = target.h < 0 ? target.y + target.h : target.y;
      return { x: x + Math.abs(target.w), y };
    }
    case 'freehand':
    case 'measure-length':
    case 'measure-area':
    case 'measure-volume': {
      if (d.kind === 'rect') {
        const x = d.w < 0 ? d.x + d.w : d.x;
        const y = d.h < 0 ? d.y + d.h : d.y;
        return { x: x + Math.abs(d.w), y };
      }
      if (d.kind === 'line') {
        return { x: Math.max(d.x1 || 0, d.x2 || 0), y: Math.min(d.y1 || 0, d.y2 || 0) };
      }
      const pts = d.points || [];
      if (!pts.length) return { x: 0, y: 0 };
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      return { x: Math.max(...xs), y: Math.min(...ys) };
    }
    default: return { x: 0, y: 0 };
  }
}

// ── LinkedFilePreview ──
// Floating card next to the cursor showing the linked file's name +
// thumbnail (for image mime types) or icon. Click anywhere on the card to
// open the file in the FileViewer.
function LinkedFilePreview({ hovered, onOpen }) {
  const { markup, clientX, clientY } = hovered;
  const file = markup.linked_file;
  if (!file) return null;

  const W = 240;
  const PADDING = 14;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let left = clientX + PADDING;
  let top  = clientY + PADDING;
  if (left + W > vw - 8) left = clientX - W - PADDING;
  left = Math.max(8, left);
  top  = Math.max(8, top);

  const isImage = (file.mime_type || '').startsWith('image/');

  const card = (
    <div
      className={styles.linkedFilePreview}
      style={{ left, top, width: W }}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
    >
      <div className={styles.linkedFileHeader}>
        <span className={styles.linkedFileLabel}>LINKED FILE</span>
      </div>
      {isImage ? (
        <img
          src={getFileDownloadUrl(file.id)}
          alt={file.name}
          className={styles.linkedFileThumb}
        />
      ) : (
        <div className={styles.linkedFileIconBox}>
          <FileGenericIcon mime={file.mime_type} />
        </div>
      )}
      <div className={styles.linkedFileMeta}>
        <div className={styles.linkedFileName} title={file.name}>{file.name}</div>
        <div className={styles.linkedFileSub}>
          {file.uploaded_by ? `${file.uploaded_by} · ` : ''}
          {file.mime_type || 'file'}
        </div>
      </div>
      <div className={styles.linkedFileFooter}>Click to open</div>
    </div>
  );
  return ReactDOM.createPortal(card, document.body);
}

// ── LinkedSheetPreview ──
// Floating card next to the cursor showing the linked sheet's number +
// title + thumbnail. Click anywhere on the card to navigate to that
// sheet (opens in a new tab via `?sheet=<id>` deep link, same mechanism
// hyperlink markups use). Visually styled like LinkedFilePreview but
// with the green accent that matches the sheet badge.
function LinkedSheetPreview({ hovered, onOpen }) {
  const { markup, clientX, clientY } = hovered;
  const sheet = markup.linked_sheet;
  if (!sheet) return null;

  const W = 240;
  const PADDING = 14;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let left = clientX + PADDING;
  let top  = clientY + PADDING;
  if (left + W > vw - 8) left = clientX - W - PADDING;
  left = Math.max(8, left);
  top  = Math.max(8, top);

  const card = (
    <div
      className={styles.linkedFilePreview}
      style={{ left, top, width: W }}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
    >
      <div className={styles.linkedFileHeader}>
        <span className={styles.linkedFileLabel} style={{ color: 'var(--green, #3ecf8e)' }}>
          LINKED SHEET
        </span>
      </div>
      <img
        src={getSheetThumbnailUrl(sheet.id, 240)}
        alt={sheet.sheet_number}
        className={styles.linkedFileThumb}
      />
      <div className={styles.linkedFileMeta}>
        <div className={styles.linkedFileName} title={sheet.title || sheet.sheet_number}>
          {sheet.sheet_number}
        </div>
        {sheet.title && sheet.title !== sheet.sheet_number && (
          <div className={styles.linkedFileSub}>{sheet.title}</div>
        )}
      </div>
      <div className={styles.linkedFileFooter}>Click to open</div>
    </div>
  );
  return ReactDOM.createPortal(card, document.body);
}

function FileGenericIcon({ mime }) {
  let tint = '#a0a0a8';
  if (mime === 'application/pdf') tint = '#f87171';
  else if (mime?.includes('word')) tint = '#4a9eff';
  else if (mime?.includes('sheet') || mime?.includes('excel')) tint = '#3ecf8e';
  return (
    <svg width="56" height="56" viewBox="0 0 20 20" fill="none">
      <path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
        stroke={tint} strokeWidth="1.4" strokeLinejoin="round"/>
      <path d="M12 2v4h4" stroke={tint} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// Floating card rendered via portal so it's never clipped by viewer overflow.
// Positioned near the cursor; resolves the target sheet's metadata and
// thumbnail URL. Click-through to the target is handled by the underlying
// hyperlink markup's onClick — the preview is hover-only.
function HyperlinkPreview({ hovered, sheets }) {
  const { markup, clientX, clientY } = hovered;
  const targetId = markup.data?.target_sheet_id;
  const target = targetId ? sheets.find((s) => s.id === targetId) : null;

  // Size + offset tuned so the card sits just below-right of the cursor
  // without obscuring the hyperlink itself.
  const W = 240;
  const H = 180;
  const PADDING = 14;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let left = clientX + PADDING;
  let top  = clientY + PADDING;
  if (left + W > vw - 8) left = clientX - W - PADDING;
  if (top  + H > vh - 8) top  = clientY - H - PADDING;
  left = Math.max(8, left);
  top  = Math.max(8, top);

  const card = (
    <div className={styles.hyperlinkPreview} style={{ left, top, width: W }}>
      <div className={styles.hyperlinkPreviewHeader}>
        <span className={styles.hyperlinkPreviewLabel}>GO TO SHEET</span>
      </div>
      {target ? (
        <>
          <img
            src={getSheetThumbnailUrl(target.id, 240)}
            alt={target.sheet_number}
            className={styles.hyperlinkPreviewThumb}
          />
          <div className={styles.hyperlinkPreviewMeta}>
            <div className={styles.hyperlinkPreviewNum}>{target.sheet_number}</div>
            {target.title && target.title !== target.sheet_number && (
              <div className={styles.hyperlinkPreviewTitle}>{target.title}</div>
            )}
            {markup.data?.raw_reference && (
              <div className={styles.hyperlinkPreviewRef}>ref: {markup.data.raw_reference}</div>
            )}
          </div>
        </>
      ) : (
        <div className={styles.hyperlinkPreviewMissing}>
          Target not available in this project
        </div>
      )}
    </div>
  );
  return ReactDOM.createPortal(card, document.body);
}

// ── Helpers ──
function normalizeRect(x, y, w, h) {
  return [w < 0 ? x + w : x, h < 0 ? y + h : y, Math.abs(w), Math.abs(h)];
}

// Comprehensive bbox computation in page-point coords. Drives marquee
// intersection. Falls back to a tiny synthetic bbox at the origin for
// markups with empty data so the marquee math doesn't crash on legacy
// or malformed rows. Mirrors MarkupPopup's getMarkupBBox but covers
// every type the canvas knows about (ellipse / polygon / polyline /
// callout / freehand / measure-*).
function getMarkupBBox(m) {
  const d = m.data || {};
  const fromPts = (pts) => {
    if (!pts || !pts.length) return { x: 0, y: 0, w: 0, h: 0 };
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  };
  const fromXYWH = () => {
    const x = (d.w || 0) < 0 ? (d.x || 0) + (d.w || 0) : (d.x || 0);
    const y = (d.h || 0) < 0 ? (d.y || 0) + (d.h || 0) : (d.y || 0);
    return { x, y, w: Math.abs(d.w || 0), h: Math.abs(d.h || 0) };
  };
  const fromXY12 = () => ({
    x: Math.min(d.x1 || 0, d.x2 || 0),
    y: Math.min(d.y1 || 0, d.y2 || 0),
    w: Math.abs((d.x2 || 0) - (d.x1 || 0)),
    h: Math.abs((d.y2 || 0) - (d.y1 || 0)),
  });

  switch (m.type) {
    case 'arrow': case 'line': case 'measure':       return fromXY12();
    case 'rect': case 'ellipse': case 'cloud':       return fromXYWH();
    case 'polyline':                                  return fromPts(d.points);
    case 'polygon':                                   return fromPts(d.points);
    case 'freehand':                                  return fromPts(d.points);
    case 'text': {
      // Boxed text carries w/h; legacy unboxed text is a single point with
      // fontSize-driven approximate width.
      if (d.w != null && d.h != null) return fromXYWH();
      const fs = d.fontSize ?? 14;
      const w  = (d.text || '').length * (fs * 0.6);
      return { x: d.x || 0, y: (d.y || 0) - fs, w, h: fs + 4 };
    }
    case 'callout': {
      // Union the box bbox with the leader endpoint (rect kind) or the
      // cloud bbox (cloud kind) so the marquee picks up the markup if
      // any part of it is inside the rectangle.
      const box = d.box || { x: 0, y: 0, w: 140, h: 40 };
      const [bx, by, bw, bh] = normalizeRect(box.x, box.y, box.w, box.h);
      let minX = bx, minY = by, maxX = bx + bw, maxY = by + bh;
      if (d.kind === 'cloud' && d.cloud) {
        const [cx, cy, cw, ch] = normalizeRect(d.cloud.x, d.cloud.y, d.cloud.w, d.cloud.h);
        minX = Math.min(minX, cx);   minY = Math.min(minY, cy);
        maxX = Math.max(maxX, cx + cw); maxY = Math.max(maxY, cy + ch);
      } else if (d.leader) {
        minX = Math.min(minX, d.leader.x); minY = Math.min(minY, d.leader.y);
        maxX = Math.max(maxX, d.leader.x); maxY = Math.max(maxY, d.leader.y);
      }
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }
    case 'measure-length':
    case 'measure-area':
    case 'measure-volume': {
      if (d.kind === 'line') return fromXY12();
      if (d.kind === 'rect') return fromXYWH();
      return fromPts(d.points);
    }
    case 'hyperlink':
      // Hyperlink markups carry their hit-rect inline as x/y/w/h.
      return fromXYWH();
    default:
      return { x: 0, y: 0, w: 0, h: 0 };
  }
}

// Standard AABB overlap: any part of the markup's bbox inside the rect
// counts as a hit. Both inputs are { x, y, w, h } in the same coord
// system (page-points). Zero-area markups still overlap if their point
// lies within the rect.
function bboxOverlapsRect(b, r) {
  return (
    b.x          <= r.x + r.w
    && b.x + b.w >= r.x
    && b.y       <= r.y + r.h
    && b.y + b.h >= r.y
  );
}

// Pick the point on the box's perimeter that's closest to the leader
// endpoint. Used so a callout's leader line attaches to the box edge
// instead of cutting through it. Coords are SVG-space (y-down).
function boxLeaderAttachment(box, leader) {
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const dx = leader.x - cx;
  const dy = leader.y - cy;
  if (dx === 0 && dy === 0) return { x: box.x + box.w, y: cy }; // degenerate
  // Walk along the ray from the box center toward the leader; find which
  // axis-bound (left/right/top/bottom) we hit first. tx, ty are the scalar
  // distances to the vertical / horizontal sides as fractions of |dx|, |dy|.
  const halfW = box.w / 2, halfH = box.h / 2;
  const tx = dx === 0 ? Infinity : halfW / Math.abs(dx);
  const ty = dy === 0 ? Infinity : halfH / Math.abs(dy);
  const t = Math.min(tx, ty);
  return { x: cx + dx * t, y: cy + dy * t };
}

// Greedy word-wrap to fit a target pixel width. Approximates the mono font
// at ~0.6 × fontSize per character — close enough for typical callout sizes.
// Returns an array of strings (one per line). Always at least one line.
function wrapTextToWidth(text, maxWidthPx, fontSize) {
  if (!text) return [''];
  const charW = fontSize * 0.6;
  const maxChars = Math.max(1, Math.floor(maxWidthPx / charW));
  const lines = [];
  for (const paragraph of String(text).split(/\r?\n/)) {
    if (!paragraph) { lines.push(''); continue; }
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) { lines.push(''); continue; }
    let cur = '';
    for (const word of words) {
      const candidate = cur ? `${cur} ${word}` : word;
      if (candidate.length <= maxChars) {
        cur = candidate;
      } else if (!cur) {
        // Single word longer than the line — hard-break.
        lines.push(word.slice(0, maxChars));
        cur = word.slice(maxChars);
      } else {
        lines.push(cur);
        cur = word;
      }
    }
    if (cur) lines.push(cur);
  }
  return lines.length ? lines : [''];
}

function cloudPath(x, y, w, h, strokeWidth = 2) {
  // Arc radius scales with stroke weight so heavier lines = bigger bumps
  const baseR  = Math.max(8, strokeWidth * 4);
  const countX = Math.max(2, Math.round(w / (baseR * 2)));
  const countY = Math.max(2, Math.round(h / (baseR * 2)));
  const rX = w / (countX * 2);
  const rY = h / (countY * 2);

  // Traverse clockwise (screen): BL → BR → TR → TL → BL
  // sweep=0 gives outward arcs on all four edges.
  let d = `M ${x} ${y + h}`;

  // Bottom: left → right  (sweep 0 = bows downward)
  for (let i = 0; i < countX; i++) {
    d += ` A ${rX} ${rX} 0 0 0 ${x + (i + 1) * 2 * rX} ${y + h}`;
  }
  // Right: bottom → top  (sweep 0 = bows rightward)
  for (let i = 0; i < countY; i++) {
    d += ` A ${rY} ${rY} 0 0 0 ${x + w} ${y + h - (i + 1) * 2 * rY}`;
  }
  // Top: right → left  (sweep 0 = bows upward)
  for (let i = 0; i < countX; i++) {
    d += ` A ${rX} ${rX} 0 0 0 ${x + w - (i + 1) * 2 * rX} ${y}`;
  }
  // Left: top → bottom  (sweep 0 = bows leftward)
  for (let i = 0; i < countY; i++) {
    d += ` A ${rY} ${rY} 0 0 0 ${x} ${y + (i + 1) * 2 * rY}`;
  }

  return d + ' Z';
}

// In-progress multi-click shapes (polyline / polygon) — used to decide when
// Enter / dblclick / tool-switch should commit or cancel.
function isMultiClickInProgress(shape) {
  if (!shape) return false;
  const k = shape.data?.kind;
  return  shape.type === 'polyline'
      || shape.type === 'polygon'
      || (shape.type === 'measure-length' && k === 'polyline')
      || (shape.type === 'measure-area'   && k === 'polygon')
      || (shape.type === 'measure-volume' && k === 'polygon');
}

// In-progress 2-point click-to-click shapes (arrow / line / measure-line).
// Distinguished from drag-mode on the same types by the absence of the
// `drawing` flag — drag sets it, click-click does not.
function isTwoPointClickInProgress(shape, drawing) {
  if (!shape || drawing) return false;
  if (shape.type === 'arrow' || shape.type === 'line') return true;
  if (shape.type === 'measure-length' && shape.data?.kind === 'line') return true;
  return false;
}

function isTrivial(shape) {
  switch (shape.type) {
    case 'arrow': case 'line': case 'measure': {
      const dx = shape.x2 - shape.x1, dy = shape.y2 - shape.y1;
      return Math.sqrt(dx*dx + dy*dy) < 5;
    }
    case 'rect': case 'ellipse': case 'cloud': case 'cloud-callout': case 'text':
      return Math.abs(shape.w) < 5 || Math.abs(shape.h) < 5;
    case 'freehand':
      return shape.points.length < 3;
    case 'polyline':
      return (shape.data?.points?.length || 0) < 2;
    case 'polygon':
      return (shape.data?.points?.length || 0) < 3;
    case 'measure-length': {
      const d = shape.data || {};
      if (d.kind === 'line') {
        const dx = d.x2 - d.x1, dy = d.y2 - d.y1;
        return Math.sqrt(dx*dx + dy*dy) < 5;
      }
      if (d.kind === 'polyline') return (d.points?.length || 0) < 2;
      if (d.kind === 'freeline') {
        if ((d.points?.length || 0) < 3) return true;
        return polylineLength(d.points) < 5;
      }
      return false;
    }
    case 'measure-area':
    case 'measure-volume': {
      const d = shape.data || {};
      if (d.kind === 'rect') {
        return Math.abs(d.w || 0) < 5 || Math.abs(d.h || 0) < 5;
      }
      if (d.kind === 'polygon')  return (d.points?.length || 0) < 3;
      if (d.kind === 'freeform') {
        if ((d.points?.length || 0) < 3) return true;
        return polygonArea(d.points) < 25;
      }
      return false;
    }
    default: return false;
  }
}

function shapeToData(shape) {
  switch (shape.type) {
    case 'arrow': case 'line': case 'measure':
      return { x1: shape.x1, y1: shape.y1, x2: shape.x2, y2: shape.y2 };
    case 'rect': case 'ellipse': case 'cloud': case 'cloud-callout': case 'text':
      // 'cloud-callout' / 'text' are in-progress drag-draw types; the
      // live preview overrides their `type` to render as a cloud or
      // a rect respectively, but the data still needs rect coords.
      // 'ellipse' shares the same bbox shape — only rendering differs.
      return { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
    case 'freehand':
      return { points: shape.points };
    case 'polyline':
    case 'polygon':
    case 'measure-length':
    case 'measure-area':
    case 'measure-volume':
      // Already lives in shape.data — just pass it through unchanged.
      return shape.data || {};
    default: return shape.data || {};
  }
}

// Applies a move or resize transformation to a markup's data.
function computeEditData(type, mode, s, dx, dy) {
  if (mode === 'move') {
    switch (type) {
      case 'arrow': case 'line': case 'measure':
        return { ...s, x1: s.x1+dx, y1: s.y1+dy, x2: s.x2+dx, y2: s.y2+dy };
      case 'rect': case 'cloud': case 'ellipse':
        return { ...s, x: s.x+dx, y: s.y+dy };
      case 'text':
        return { ...s, x: s.x+dx, y: s.y+dy };
      case 'callout': {
        // Translate every piece together. For rect kind: box + leader
        // endpoint. For cloud kind: box + cloud (no leader endpoint —
        // the leader is auto-routed between the two pieces).
        const box = s.box || { x: 0, y: 0, w: 140, h: 40 };
        const next = { ...s, box: { ...box, x: box.x + dx, y: box.y + dy } };
        if (s.kind === 'cloud') {
          const cloud = s.cloud || { x: box.x + box.w + 80, y: box.y + 30, w: 120, h: 80 };
          next.cloud = { ...cloud, x: cloud.x + dx, y: cloud.y + dy };
        } else {
          const leader = s.leader || { x: box.x + 100, y: box.y - 60 };
          next.leader = { x: leader.x + dx, y: leader.y + dy };
        }
        return next;
      }
      case 'freehand':
        return { ...s, points: (s.points||[]).map((p) => ({ x: p.x+dx, y: p.y+dy })) };
      case 'polyline':
      case 'polygon':
        return { ...s, points: (s.points||[]).map((p) => ({ x: p.x+dx, y: p.y+dy })) };
      case 'measure-length': {
        if (s.kind === 'line') {
          return { ...s, x1: s.x1+dx, y1: s.y1+dy, x2: s.x2+dx, y2: s.y2+dy };
        }
        return { ...s, points: (s.points||[]).map((p) => ({ x: p.x+dx, y: p.y+dy })) };
      }
      case 'measure-area':
      case 'measure-volume': {
        if (s.kind === 'rect') {
          return { ...s, x: s.x+dx, y: s.y+dy };
        }
        return { ...s, points: (s.points||[]).map((p) => ({ x: p.x+dx, y: p.y+dy })) };
      }
      default: return s;
    }
  }
  if (type === 'callout') {
    // Leader handle (rect kind only): drag just the leader endpoint, leave
    // the box alone.
    if (mode === 'leader') {
      const leader = s.leader || { x: 0, y: 0 };
      return { ...s, leader: { x: leader.x + dx, y: leader.y + dy } };
    }
    // Cloud-only translation (cloud kind only): drag the cloud body to
    // move just the cloud onto the area being called out. Box stays put.
    if (mode === 'cloud-move') {
      const cloud = s.cloud || { x: 0, y: 0, w: 120, h: 80 };
      return { ...s, cloud: { ...cloud, x: cloud.x + dx, y: cloud.y + dy } };
    }
    // Box-only translation (cloud kind only).
    if (mode === 'box-move') {
      const box = s.box || { x: 0, y: 0, w: 140, h: 40 };
      return { ...s, box: { ...box, x: box.x + dx, y: box.y + dy } };
    }
    // Cloud-resize handles (cloud kind only): same NW/N/NE/E/SE/S/SW/W as
    // box-resize but operating on s.cloud.
    if (mode.startsWith('cloud-resize-')) {
      const cloud = s.cloud || { x: 0, y: 0, w: 120, h: 80 };
      const [nx, ny, nw, nh] = normalizeRect(cloud.x, cloud.y, cloud.w, cloud.h);
      const dirs = mode.replace('cloud-resize-', '');
      let left = nx, top = ny, right = nx + nw, bottom = ny + nh;
      if (dirs.includes('w')) left   = nx + dx;
      if (dirs.includes('e')) right  = nx + nw + dx;
      if (dirs.includes('n')) top    = ny + dy;
      if (dirs.includes('s')) bottom = ny + nh + dy;
      if (right - left < 20) right  = left + 20;
      if (bottom - top < 16) bottom = top + 16;
      return {
        ...s,
        cloud: { x: left, y: top, w: right - left, h: bottom - top },
      };
    }
    // Box-resize handles: same NW/N/NE/E/SE/S/SW/W convention as rect.
    if (mode.startsWith('resize-')) {
      const box = s.box || { x: 0, y: 0, w: 140, h: 40 };
      const [nx, ny, nw, nh] = normalizeRect(box.x, box.y, box.w, box.h);
      const dirs = mode.replace('resize-', '');
      let left = nx, top = ny, right = nx + nw, bottom = ny + nh;
      if (dirs.includes('w')) left   = nx + dx;
      if (dirs.includes('e')) right  = nx + nw + dx;
      if (dirs.includes('n')) top    = ny + dy;
      if (dirs.includes('s')) bottom = ny + nh + dy;
      if (right - left < 20) right  = left + 20;
      if (bottom - top < 16) bottom = top + 16;
      return {
        ...s,
        box: { x: left, y: top, w: right - left, h: bottom - top },
      };
    }
  }
  if (type === 'arrow' || type === 'line' || type === 'measure') {
    if (mode === 'resize-start') return { ...s, x1: s.x1+dx, y1: s.y1+dy };
    if (mode === 'resize-end')   return { ...s, x2: s.x2+dx, y2: s.y2+dy };
  }
  if (type === 'measure-length') {
    if (s.kind === 'line') {
      if (mode === 'resize-start') return { ...s, x1: s.x1+dx, y1: s.y1+dy };
      if (mode === 'resize-end')   return { ...s, x2: s.x2+dx, y2: s.y2+dy };
    } else if (mode.startsWith('vertex-')) {
      const idx = parseInt(mode.slice('vertex-'.length), 10);
      const pts = (s.points || []).map((p, i) =>
        i === idx ? { x: p.x + dx, y: p.y + dy } : p
      );
      return { ...s, points: pts };
    }
  }
  if (type === 'measure-area' || type === 'measure-volume') {
    if (s.kind === 'rect') {
      const [nx, ny, nw, nh] = normalizeRect(s.x, s.y, s.w, s.h);
      const dirs = mode.replace('resize-', '');
      let left = nx, top = ny, right = nx + nw, bottom = ny + nh;
      if (dirs.includes('w')) left   = nx + dx;
      if (dirs.includes('e')) right  = nx + nw + dx;
      if (dirs.includes('n')) top    = ny + dy;
      if (dirs.includes('s')) bottom = ny + nh + dy;
      if (right - left < 5)  right  = left + 5;
      if (bottom - top < 5)  bottom = top + 5;
      return { ...s, x: left, y: top, w: right - left, h: bottom - top };
    }
    if (s.kind === 'polygon' && mode.startsWith('vertex-')) {
      const idx = parseInt(mode.slice('vertex-'.length), 10);
      const pts = (s.points || []).map((p, i) =>
        i === idx ? { x: p.x + dx, y: p.y + dy } : p
      );
      return { ...s, points: pts };
    }
  }
  if (type === 'polygon' && mode.startsWith('vertex-')) {
    const idx = parseInt(mode.slice('vertex-'.length), 10);
    const pts = (s.points || []).map((p, i) =>
      i === idx ? { x: p.x + dx, y: p.y + dy } : p
    );
    return { ...s, points: pts };
  }
  if (type === 'rect' || type === 'cloud' || type === 'ellipse') {
    const [nx, ny, nw, nh] = normalizeRect(s.x, s.y, s.w, s.h);
    const dirs = mode.replace('resize-', '');
    let left = nx, top = ny, right = nx + nw, bottom = ny + nh;
    if (dirs.includes('w')) left   = nx + dx;
    if (dirs.includes('e')) right  = nx + nw + dx;
    if (dirs.includes('n')) top    = ny + dy;
    if (dirs.includes('s')) bottom = ny + nh + dy;
    if (right - left < 5)  right  = left + 5;
    if (bottom - top < 5)  bottom = top + 5;
    return { ...s, x: left, y: top, w: right - left, h: bottom - top };
  }
  return s;
}
